<template>
  <div class="theme-page">
    <section class="theme-selector page-card">
      <div class="theme-selector__summary">
        <div class="page-card__title">主题方案</div>
        <div class="page-card__desc">{{ themes.length }} 套小程序主题</div>
      </div>

      <div v-if="loading" class="theme-selector__state">加载中...</div>
      <div v-else-if="themes.length === 0" class="theme-selector__state">还没有主题</div>
      <div v-else class="theme-list">
        <button
          v-for="item in themes"
          :key="item.themeCode"
          type="button"
          class="theme-card"
          :class="{ 'theme-card--active': selectedThemeCode === item.themeCode }"
          @click="selectTheme(item.themeCode)"
        >
          <div class="theme-card__art" :style="buildThemePreviewStyle(item.themeCode, item.previewImageUrl)"></div>
          <div class="theme-card__main">
            <div class="theme-card__head">
              <strong>{{ item.themeName }}</strong>
              <span v-if="item.isDefault === 1" class="theme-card__default">默认</span>
            </div>
            <div class="theme-card__meta">{{ item.themeCode }}</div>
          </div>
        </button>
      </div>

      <div class="theme-selector__actions">
        <button class="ghost-button" :disabled="loading" @click="loadAll">刷新</button>
        <button class="action-button" @click="startCreateTheme">新建主题</button>
      </div>
    </section>

    <main class="theme-workbench page-card">
      <div class="workbench-head">
        <div class="workbench-title">
          <div class="theme-preview-dot" :style="buildThemePreviewStyle(themeDraft.themeCode, themeDraft.previewImageUrl)"></div>
          <div class="theme-identity-editor">
            <input v-model.trim="themeDraft.themeName" class="theme-name-input" placeholder="主题名称" />
            <div class="workbench-title__meta">
              <span>{{ selectedThemeCode || "尚未保存" }}</span>
              <span :class="themeDraft.status === 1 ? 'status-dot status-dot--active' : 'status-dot'">
                {{ themeDraft.status === 1 ? "已启用" : "已停用" }}
              </span>
              <span v-if="themeDraft.isDefault === 1" class="status-dot status-dot--default">默认主题</span>
            </div>
          </div>
        </div>
        <div class="head-actions">
          <input
            v-if="!isEditingTheme"
            v-model.trim="themeDraft.themeCode"
            class="theme-code-input"
            placeholder="新主题编码"
          />
          <button v-if="selectedThemeCode" class="danger-button" :disabled="deletingTheme" @click="deleteSelectedTheme">
            {{ deletingTheme ? "删除中..." : "删除主题" }}
          </button>
          <button class="ghost-button" @click="resetThemeDraft">重置</button>
          <button class="action-button" :disabled="savingTheme" @click="saveThemeDraft">{{ savingTheme ? "保存中..." : (isEditingTheme ? "保存名称" : "创建主题") }}</button>
        </div>
      </div>

      <div v-if="message.text" class="theme-message" :class="message.type === 'error' ? 'theme-message--error' : 'theme-message--success'">
        {{ message.text }}
      </div>

      <div v-if="false" class="theme-basic-workbench">
        <section class="theme-basic-preview">
          <div class="theme-basic-preview__art" :style="buildThemePreviewStyle(themeDraft.themeCode, themeDraft.previewImageUrl)">
            <div class="theme-basic-preview__chrome">
              <span></span><span></span><span></span>
            </div>
            <div class="theme-basic-preview__content">
              <strong>{{ themeDraft.themeName || "未命名主题" }}</strong>
              <span>{{ themeDraft.remark || "小程序界面主题" }}</span>
            </div>
          </div>
          <dl class="theme-facts">
            <div>
              <dt>客户端</dt>
              <dd>{{ themeDraft.clientType || "MINI_APP" }}</dd>
            </div>
            <div>
              <dt>版本</dt>
              <dd>v{{ themeDraft.versionNo || 1 }}</dd>
            </div>
            <div>
              <dt>图标包</dt>
              <dd>{{ iconPacks.length }} 套</dd>
            </div>
          </dl>
        </section>

        <section class="theme-basic-editor">
          <div class="panel-head">
            <div>
              <h3>基础设置</h3>
              <p>主题编码保存后不可修改。</p>
            </div>
          </div>
          <div class="theme-basic-form">
            <label class="field">
              <span>主题名称</span>
              <input v-model.trim="themeDraft.themeName" placeholder="例如：雨林探索" />
            </label>
            <label class="field">
              <span>主题编码</span>
              <input v-model.trim="themeDraft.themeCode" :disabled="isEditingTheme" placeholder="rainforest" />
            </label>
            <label class="field field--wide">
              <span>主题说明</span>
              <input v-model.trim="themeDraft.remark" placeholder="填写主题定位或适用场景" />
            </label>
            <label class="field field--wide">
              <span>商店预览图</span>
              <div class="input-with-action">
                <input v-model.trim="themeDraft.previewImageUrl" placeholder="主题商店展示的预览图地址" />
                <label class="upload-button upload-button--field">
                  上传
                  <input type="file" accept="image/*" @change="uploadThemePreviewImage" />
                </label>
              </div>
            </label>
            <label class="field">
              <span>版本号</span>
              <input v-model.number="themeDraft.versionNo" type="number" min="1" step="1" />
            </label>
            <label class="setting-toggle">
              <span>启用主题<small>允许用户选择此主题</small></span>
              <input v-model="themeDraft.status" type="checkbox" :true-value="1" :false-value="0" />
              <i></i>
            </label>
            <label class="setting-toggle">
              <span>设为默认<small>新用户优先使用此主题</small></span>
              <input v-model="themeDraft.isDefault" type="checkbox" :true-value="1" :false-value="0" />
              <i></i>
            </label>
          </div>
        </section>
      </div>

      <div v-else-if="false" class="layout-workbench">
        <section class="layout-preset-panel">
          <div class="panel-head">
            <div>
              <h3>整套页面布局</h3>
              <p>一次切换首页、服务、日历、广场和我的页面。</p>
            </div>
            <button class="action-button" :disabled="savingLayout || !selectedThemeCode" @click="saveLayoutDraft">
              {{ savingLayout ? "保存中..." : "保存页面布局" }}
            </button>
          </div>

          <div v-if="!selectedThemeCode" class="layout-empty-state">先保存主题，再配置页面布局</div>
          <template v-else>
            <div class="layout-preset-grid">
              <button
                v-for="preset in layoutPresets"
                :key="preset.layoutCode"
                type="button"
                class="layout-preset-card"
                :class="{ 'layout-preset-card--active': selectedLayoutCode === preset.layoutCode }"
                @click="applyLayoutPreset(preset.layoutCode)"
              >
                <span class="layout-preset-card__preview" :class="'layout-preset-card__preview--' + preset.layoutCode">
                  <i></i><i></i><i></i><i></i>
                </span>
                <span class="layout-preset-card__copy">
                  <strong>{{ preset.name }}</strong>
                  <small>{{ preset.desc }}</small>
                </span>
                <span class="layout-preset-card__check">✓</span>
              </button>
            </div>

            <div class="layout-section-title">
              <div>
                <strong>页面组件</strong>
                <span>需要时可单独调整某个 Tab</span>
              </div>
            </div>
            <div class="tab-route-grid">
              <label v-for="page in themeTabPages" :key="page.pageCode" class="tab-route-item">
                <span>{{ page.name }}</span>
                <select :value="resolveRouteDraftCode(page.pageCode)" @change="updateRouteDraft(page.pageCode, $event.target.value)">
                  <option v-for="preset in layoutPresets" :key="preset.routeCode" :value="preset.routeCode">{{ preset.name }}</option>
                </select>
              </label>
            </div>
          </template>
        </section>

        <section class="home-order-panel">
          <div class="panel-head">
            <div>
              <h3>首页区块顺序</h3>
              <p>从上到下对应小程序首页的显示顺序。</p>
            </div>
          </div>
          <div class="home-section-list">
            <div v-for="(section, index) in homeSectionDrafts" :key="section.key" class="home-section-row">
              <span class="home-section-row__index">{{ index + 1 }}</span>
              <div class="home-section-row__main">
                <strong>{{ resolveHomeSectionName(section.key) }}</strong>
                <small>{{ section.key }}</small>
              </div>
              <select v-model="section.variant" class="home-section-row__variant">
                <option v-for="variant in resolveHomeSectionVariants(section.key)" :key="variant" :value="variant">{{ resolveVariantName(variant) }}</option>
              </select>
              <div class="order-actions">
                <button type="button" title="上移" :disabled="index === 0" @click="moveHomeSection(index, -1)">↑</button>
                <button type="button" title="下移" :disabled="index === homeSectionDrafts.length - 1" @click="moveHomeSection(index, 1)">↓</button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div v-else-if="false" class="workbench-body">
        <section class="feature-panel">
          <div class="panel-head">
            <div>
              <h3>全局功能库</h3>
              <p>名称、跳转和显示状态对所有主题生效。</p>
            </div>
            <button class="ghost-button" @click="startCreateFeature">新增功能</button>
          </div>

          <div class="feature-list">
            <div
              v-for="item in features"
              :key="item.featureKey"
              class="feature-row"
              :class="{ 'feature-row--active': selectedFeatureKey === item.featureKey }"
              @click="selectFeature(item.featureKey)"
            >
              <div class="feature-icon">
                <img v-if="resolveFeaturePreview(item)" :src="resolvePreviewUrl(resolveFeaturePreview(item))" alt="" />
                <span v-else>{{ item.defaultIconText || item.name.slice(0, 1) }}</span>
              </div>
              <div class="feature-row__main">
                <div class="feature-row__title">{{ item.name }}</div>
                <div class="feature-row__meta">{{ item.featureKey }} · {{ item.url }}</div>
              </div>
              <span :class="item.status === 1 ? 'pill pill--green' : 'pill'">{{ item.status === 1 ? "显示" : "隐藏" }}</span>
              <button type="button" class="row-action" @click.stop="selectFeature(item.featureKey)">编辑</button>
            </div>
          </div>
        </section>

        <section class="feature-detail-panel">
          <div class="panel-head panel-head--sticky">
            <div>
              <h3>{{ featureDraft.featureKey ? "编辑功能" : "新增功能" }}</h3>
            </div>
            <div class="head-actions">
              <button class="ghost-button" :disabled="savingThemeIcon || !canSaveThemeIcon" @click="saveThemeIconDraft">
                {{ savingThemeIcon ? "保存中..." : "保存图标" }}
              </button>
              <button class="action-button" :disabled="savingFeature" @click="saveFeatureDraft">{{ savingFeature ? "保存中..." : "保存功能" }}</button>
            </div>
          </div>

          <div class="icon-source-bar">
            <span class="icon-source-bar__label">预览图标包</span>
            <button
              v-for="pack in iconPacks"
              :key="pack.packCode"
              type="button"
              class="icon-pack-chip"
              :class="{ 'icon-pack-chip--active': selectedIconPackCode === pack.packCode }"
              @click="selectedIconPackCode = pack.packCode"
            >
              {{ pack.packName }}
              <small v-if="pack.isDefault === 1">默认</small>
            </button>
            <span v-if="iconPacks.length === 0" class="icon-source-bar__empty">当前主题无图标包</span>
          </div>

          <div class="feature-detail-body">
            <div class="phone-preview">
              <div class="phone-card">
                <div class="phone-icon">
                  <img v-if="resolveFeatureEditorIcon().url" :src="resolvePreviewUrl(resolveFeatureEditorIcon().url)" alt="" />
                  <span v-else>{{ resolveFeatureEditorIcon().text }}</span>
                </div>
                <div class="phone-title">{{ featureDraft.featureName || "选择一个功能" }}</div>
                <div class="phone-desc">{{ resolveFeatureEditorIcon().source }}</div>
              </div>
            </div>

            <div class="icon-priority-line">
              <span>图标包</span><i>›</i><span>主题后备</span><i>›</i><span>功能默认</span>
            </div>
            <div class="section-divider section-divider--first">主题后备图标</div>
            <div class="editor-grid">
              <label class="field">
                <span>图标类型</span>
                <select v-model="themeIconDraft.iconType">
                  <option value="image">图片</option>
                  <option value="text">文字</option>
                </select>
              </label>
              <label class="field">
                <span>覆盖状态</span>
                <select v-model.number="themeIconDraft.status">
                  <option :value="1">启用覆盖</option>
                  <option :value="0">停用覆盖</option>
                </select>
              </label>
              <label class="field field--wide">
                <span>主题图标地址</span>
                <input v-model.trim="themeIconDraft.iconUrl" placeholder="/uploads/theme-icons/xxx.png" />
              </label>
              <label class="field">
                <span>文字图标</span>
                <input v-model.trim="themeIconDraft.iconText" placeholder="可选" />
              </label>
            </div>

            <div class="inline-upload-bar">
              <label class="upload-button">
                上传主题图标
                <input type="file" accept="image/*" @change="uploadThemeOverrideIcon" />
              </label>
              <button class="ghost-button" @click="clearThemeIcon">清空覆盖</button>
            </div>

            <div class="section-divider">功能基础</div>
            <div class="editor-grid">
              <label class="field">
                <span>功能编码</span>
                <input v-model.trim="featureDraft.featureKey" :disabled="isEditingFeature" placeholder="feeding-plan" />
              </label>
              <label class="field">
                <span>功能名称</span>
                <input v-model.trim="featureDraft.featureName" placeholder="喂食计划" />
              </label>
              <label class="field">
                <span>排序</span>
                <input v-model.number="featureDraft.sortNo" type="number" step="10" />
              </label>
              <label class="field">
                <span>显示</span>
                <select v-model.number="featureDraft.status">
                  <option :value="1">显示</option>
                  <option :value="0">隐藏</option>
                </select>
              </label>
              <label class="field field--wide">
                <span>跳转地址</span>
                <input v-model.trim="featureDraft.url" placeholder="/pages/feeding-plan/index" />
              </label>
              <label class="field">
                <span>默认图标类型</span>
                <select v-model="featureDraft.defaultIconType">
                  <option value="text">文字</option>
                  <option value="image">图片</option>
                </select>
              </label>
              <label class="field">
                <span>默认文字</span>
                <input v-model.trim="featureDraft.defaultIconText" placeholder="计" />
              </label>
              <label class="field field--wide">
                <span>默认图标地址</span>
                <input v-model.trim="featureDraft.defaultIconUrl" placeholder="/uploads/theme-icons/xxx.png" />
              </label>
              <label class="field field--wide">
                <span>备注</span>
                <input v-model.trim="featureDraft.remark" placeholder="可选" />
              </label>
            </div>

            <div class="inline-upload-bar">
              <label class="upload-button">
                上传默认图标
                <input type="file" accept="image/*" @change="uploadFeatureDefaultIcon" />
              </label>
            </div>
          </div>
        </section>
      </div>

      <div v-else-if="true" class="page-icon-workbench">
        <aside class="page-catalog-panel">
          <div class="panel-head">
            <div>
              <h3>主题界面</h3>
              <p>只展示当前主题实际适用的界面。</p>
            </div>
            <button class="ghost-button" :disabled="!selectedThemeCode" @click="loadThemeAssets(selectedThemeCode)">刷新</button>
          </div>
          <div v-if="pageAssetPages.length === 0" class="page-state">当前主题还没有登记界面图标</div>
          <div v-else class="page-catalog-list">
            <button
              v-for="page in pageAssetPages"
              :key="page.pageCode"
              type="button"
              class="page-catalog-row"
              :class="{ 'page-catalog-row--active': selectedAssetPageCode === page.pageCode }"
              @click="selectAssetPage(page.pageCode)"
            >
              <span class="page-catalog-row__icon">{{ page.name.slice(0, 1) }}</span>
              <span class="page-catalog-row__main">
                <strong>{{ page.name }}</strong>
                <small>{{ page.pagePath }}</small>
              </span>
              <span class="page-catalog-row__count">{{ page.count }}</span>
            </button>
          </div>
        </aside>

        <section class="page-icon-list-panel">
          <div class="panel-head">
            <div>
              <h3>{{ selectedAssetPage ? selectedAssetPage.name : "界面图标" }}</h3>
              <p>{{ selectedAssetPage ? selectedAssetPage.pagePath : "先选择界面" }}</p>
            </div>
            <span v-if="compileTimeAssetCount > 0" class="compile-time-note">{{ compileTimeAssetCount }} 项需重新发布</span>
          </div>
          <div v-if="visibleInterfaceIcons.length === 0" class="page-state">该界面没有可配置图标</div>
          <div v-else class="page-icon-grid">
            <button
              v-for="item in visibleInterfaceIcons"
              :key="item.editorKey"
              type="button"
              class="page-icon-card"
              :class="{ 'page-icon-card--active': selectedAssetKey === item.editorKey }"
              @click="selectInterfaceIcon(item)"
            >
              <span class="page-icon-card__preview" :class="{ 'page-icon-card__preview--background': item.assetType === 'background' }">
                <img v-if="resolveAssetDisplayUrl(item)" :src="resolvePreviewUrl(resolveAssetDisplayUrl(item))" alt="" />
                <span v-else>{{ buildAssetFallbackText(item) }}</span>
              </span>
              <span class="page-icon-card__copy">
                <strong>{{ item.assetName }}</strong>
                <small>{{ item.configured === 1 ? "已覆盖" : "使用默认" }}</small>
              </span>
              <span v-if="item.sourceType === 'FEATURE'" class="page-icon-card__mode">常用功能</span>
              <span v-else-if="item.runtimeMode === 'COMPILE_TIME'" class="page-icon-card__mode">需发布</span>
            </button>
          </div>
        </section>

        <section class="asset-editor-panel">
          <div class="panel-head">
            <div>
              <h3>{{ assetDraft.assetName || "选择图标" }}</h3>
              <p>{{ assetDraft.assetKey || "选择左侧界面中的图标" }}</p>
            </div>
            <button class="action-button" :disabled="savingThemeAsset || !canSaveThemeAsset" @click="saveThemeAssetDraft">
              {{ savingThemeAsset ? "保存中..." : "保存当前图标" }}
            </button>
          </div>

          <div class="asset-preview-block">
            <div v-if="isBackgroundAsset" class="asset-background-preview" :style="buildAssetBackgroundPreviewStyle()">
              <span v-if="!resolveAssetDisplayUrl(assetDraft)">背景图预览</span>
            </div>
            <div v-else class="asset-entry-preview">
              <div class="asset-entry-icon">
                <img v-if="resolveAssetDisplayUrl(assetDraft)" :src="resolvePreviewUrl(resolveAssetDisplayUrl(assetDraft))" alt="" />
                <span v-else>{{ buildAssetFallbackText(assetDraft) }}</span>
              </div>
              <div>
                <strong>{{ assetDraft.assetName || "界面图标" }}</strong>
                <p>{{ assetDraft.assetUrl ? "当前主题覆盖图" : "当前使用目录默认图" }}</p>
              </div>
            </div>
          </div>

          <div class="editor-grid">
            <label class="field">
              <span>图标编码</span>
              <input v-model.trim="assetDraft.assetKey" disabled placeholder="page.background" />
            </label>
            <label class="field">
              <span>所在界面</span>
              <input :value="assetDraft.pageName || assetDraft.pageCode" disabled />
            </label>
            <label class="field">
              <span>状态</span>
              <select v-model.number="assetDraft.status">
                <option :value="1">启用</option>
                <option :value="0">停用</option>
              </select>
            </label>
            <label class="field field--wide">
              <span>当前主题覆盖图</span>
              <input v-model.trim="assetDraft.assetUrl" placeholder="/uploads/theme-icons/xxx.png" />
            </label>
            <label class="field field--wide">
              <span>目录默认图</span>
              <input :value="assetDraft.defaultAssetUrl" disabled placeholder="此图标没有默认图片" />
            </label>
          </div>

          <div class="upload-card">
            <div class="upload-preview upload-preview--soft">
              <img v-if="resolveAssetDisplayUrl(assetDraft)" :src="resolvePreviewUrl(resolveAssetDisplayUrl(assetDraft))" alt="" />
              <span v-else>图</span>
            </div>
            <div>
              <strong>{{ isBackgroundAsset ? "上传背景图" : "替换当前图标" }}</strong>
              <p v-if="assetDraft.runtimeMode === 'COMPILE_TIME'">原生导航资源需要重新打包发布后生效。</p>
              <p v-else>保存后，小程序下次进入该界面时生效。</p>
            </div>
            <label class="upload-button">
              上传图片
              <input type="file" accept="image/*" @change="uploadThemeAssetImage" />
            </label>
            <button v-if="assetDraft.configured === 1 || assetDraft.assetUrl" type="button" class="ghost-button" @click="restoreCurrentIconDefault">恢复默认</button>
          </div>
        </section>
      </div>

      <div v-else-if="false" class="pet-type-workbench">
        <section class="asset-panel">
          <div class="panel-head">
            <div>
              <h3>宠物类型头像</h3>
              <p>为当前主题覆盖宠物类型图片；未覆盖时使用宠物类型默认图。</p>
            </div>
            <button class="ghost-button" :disabled="!selectedThemeCode" @click="loadThemePetTypeImages(selectedThemeCode)">刷新</button>
          </div>

          <div v-if="themePetTypeImages.length === 0" class="page-state">选择主题后可配置宠物类型头像</div>
          <div v-else class="asset-list">
            <div
              v-for="item in themePetTypeImages"
              :key="item.typeCode"
              class="asset-row"
              :class="{ 'asset-row--active': selectedPetTypeCode === item.typeCode }"
              @click="selectPetTypeImage(item.typeCode)"
            >
              <div class="asset-thumb">
                <img v-if="item.imageUrl || item.defaultImageUrl" :src="resolvePreviewUrl(item.imageUrl || item.defaultImageUrl)" alt="" />
                <span v-else>{{ (item.typeName || item.typeCode || '宠').slice(0, 1) }}</span>
              </div>
              <div class="asset-row__main">
                <div class="asset-row__title">{{ item.typeName || item.typeCode }}</div>
                <div class="asset-row__meta">{{ item.typeCode }} · {{ item.levelNo || 1 }}级</div>
              </div>
              <span :class="item.status === 1 ? 'pill pill--green' : 'pill'">{{ item.status === 1 ? "覆盖" : "默认" }}</span>
              <button type="button" class="row-action" @click.stop="selectPetTypeImage(item.typeCode)">编辑</button>
            </div>
          </div>
        </section>

        <section class="asset-editor-panel">
          <div class="panel-head">
            <div>
              <h3>{{ petTypeImageDraft.typeName || "选择宠物类型" }}</h3>
              <p>{{ selectedThemeCode || "先选择主题" }} · {{ petTypeImageDraft.typeCode || "未选择类型" }}</p>
            </div>
            <button class="action-button" :disabled="savingPetTypeImage || !canSavePetTypeImage" @click="savePetTypeImageDraft">
              {{ savingPetTypeImage ? "保存中..." : "保存头像" }}
            </button>
          </div>

          <div class="asset-preview-block">
            <div class="pet-type-image-preview">
              <img v-if="petTypeImageDraft.imageUrl || petTypeImageDraft.defaultImageUrl" :src="resolvePreviewUrl(petTypeImageDraft.imageUrl || petTypeImageDraft.defaultImageUrl)" alt="" />
              <span v-else>宠</span>
            </div>
          </div>

          <div class="editor-grid">
            <label class="field">
              <span>类型编码</span>
              <input v-model.trim="petTypeImageDraft.typeCode" disabled />
            </label>
            <label class="field">
              <span>类型名称</span>
              <input v-model.trim="petTypeImageDraft.typeName" disabled />
            </label>
            <label class="field field--wide">
              <span>主题头像地址</span>
              <input v-model.trim="petTypeImageDraft.imageUrl" placeholder="/uploads/theme-icons/xxx.png" />
            </label>
            <label class="field field--wide">
              <span>默认头像地址</span>
              <input v-model.trim="petTypeImageDraft.defaultImageUrl" disabled />
            </label>
            <label class="field">
              <span>覆盖状态</span>
              <select v-model.number="petTypeImageDraft.status">
                <option :value="1">启用覆盖</option>
                <option :value="0">使用默认</option>
              </select>
            </label>
          </div>

          <div class="upload-card">
            <div class="upload-preview upload-preview--soft">
              <img v-if="petTypeImageDraft.imageUrl" :src="resolvePreviewUrl(petTypeImageDraft.imageUrl)" alt="" />
              <span v-else>图</span>
            </div>
            <div>
              <strong>上传主题宠物头像</strong>
              <p>上传后会填入当前主题下这个宠物类型的覆盖头像。</p>
            </div>
            <label class="upload-button">
              上传图片
              <input type="file" accept="image/*" @change="uploadThemePetTypeImage" />
            </label>
          </div>
        </section>
      </div>

      <div v-else class="style-workbench">
        <section class="style-panel">
          <div class="panel-head">
            <div>
              <h3>界面样式</h3>
              <p>这里不是写 CSS，而是维护小程序可识别的主题样式项。</p>
            </div>
            <button class="ghost-button" :disabled="!selectedThemeCode" @click="loadThemeStyleTokens(selectedThemeCode)">刷新</button>
          </div>

          <div v-if="themeStyleTokens.length === 0" class="page-state">选择主题后可配置界面样式</div>
          <div v-else class="style-token-grid">
            <label
              v-for="item in themeStyleTokens"
              :key="item.tokenKey"
              class="style-token-card"
            >
              <span>{{ item.tokenName }}</span>
              <small>{{ item.tokenKey }}</small>
              <input
                v-if="item.valueType === 'color'"
                v-model.trim="item.tokenValue"
                type="color"
              />
              <input
                v-else-if="item.valueType === 'number'"
                v-model.trim="item.tokenValue"
                type="number"
                step="0.01"
              />
              <input
                v-else
                v-model.trim="item.tokenValue"
              />
            </label>
          </div>
          <div class="style-action-bar">
            <button class="action-button" :disabled="savingStyleTokens || !selectedThemeCode" @click="saveStyleTokenDrafts">
              {{ savingStyleTokens ? "保存中..." : "保存界面样式" }}
            </button>
          </div>
        </section>

        <section class="style-preview-panel">
          <div class="panel-head">
            <div>
              <h3>首页效果预览</h3>
              <p>大致预览卡片、按钮和文字色，实际以小程序为准。</p>
            </div>
          </div>
          <div class="style-preview-phone" :style="buildStylePreviewPageStyle()">
            <div class="style-preview-card" :style="buildStylePreviewCardStyle()">
              <div class="style-preview-title" :style="{ color: resolveStyleTokenValue('textPrimary', '#111827') }">常用功能</div>
              <div class="style-preview-desc" :style="{ color: resolveStyleTokenValue('textSecondary', '#64748b') }">主题控制卡片背景、透明度和主色。</div>
              <div class="style-preview-button" :style="{ background: resolveStyleTokenValue('primaryColor', '#2B5FFF') }">管理</div>
            </div>
          </div>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import {
  deleteTheme,
  fetchAppFeatures,
  fetchThemeAssets,
  fetchThemeDetail,
  fetchThemeFeatureIcons,
  fetchThemeList,
  fetchThemePetTypeImages,
  fetchThemeStyleTokens,
  saveAppFeature,
  saveTheme,
  saveThemeAsset,
  saveThemeFeatureIcon,
  saveThemePetTypeImage,
  saveThemeStyleToken,
  uploadFeatureIcon
} from "../api/appTheme"
import { resolveBackendAssetUrl } from "../utils/apiEndpoint"

function createThemeDraft() {
  return {
    themeCode: "",
    themeName: "",
    clientType: "MINI_APP",
    previewImageUrl: "",
    status: 1,
    isDefault: 0,
    versionNo: 1,
    remark: "",
    slots: null
  }
}

const LAYOUT_PRESETS = [
  {
    layoutCode: "repti_lover",
    routeCode: "repti-lover",
    name: "Repti Lover",
    desc: "爬宠爱好者专属内容布局"
  }
]

const THEME_TAB_PAGES = [
  { pageCode: "home", name: "首页", slug: "home", pagePath: "/pages/app/index" },
  { pageCode: "service_shop", name: "服务", slug: "service-shop", pagePath: "/pages/service-shop/index" },
  { pageCode: "calendar", name: "日历", slug: "calendar", pagePath: "/pages/calendar/index" },
  { pageCode: "square", name: "广场", slug: "square", pagePath: "/pages/square/index" },
  { pageCode: "mine", name: "我的", slug: "mine", pagePath: "/pages/mine/index" }
]

const HOME_SECTION_DEFINITIONS = [
  { key: "petPanel", name: "宠物面板", variants: ["expanded", "compact", "collapsed", "hero"] },
  { key: "actionBar", name: "快捷操作", variants: ["compact", "minimal"] },
  { key: "snakeTool", name: "养护工具", variants: ["auto", "hidden"] },
  { key: "quickFeature", name: "常用功能", variants: ["grid", "focus", "compact"] }
]

const HOME_INTERFACE_ICON_FALLBACKS = [
  { assetKey: "action.batch-feeding.icon", assetName: "快捷操作 - 批量喂食", fallbackText: "批", sortNo: 20 },
  { assetKey: "action.album.icon", assetName: "快捷操作 - 宠物相册", fallbackText: "相", sortNo: 30 },
  { assetKey: "action.batch-weight.icon", assetName: "快捷操作 - 批量体重", fallbackText: "重", sortNo: 40 },
  { assetKey: "action.ai-assistant.icon", assetName: "快捷操作 - AI 助手", fallbackText: "AI", sortNo: 50 },
  { assetKey: "action.snake-calculator.icon", assetName: "蛇类喂食计算器", fallbackText: "算", sortNo: 60 },
  { assetKey: "action.feature-manager.icon", assetName: "管理常用功能", fallbackText: "+", sortNo: 70 }
]

const REPTI_LOVER_INTERFACE_ICON_FALLBACKS = [
  {
    pageCode: "calendar", pageName: "日历", pagePath: "/pages/calendar/index", pageGroup: "main",
    items: [
      ["quick.feeding-plan.icon", "喂食计划", "计"],
      ["quick.pet-profile.icon", "宠物资料", "宠"],
      ["quick.feeding-record.icon", "喂养记录", "养"],
      ["quick.weight-record.icon", "体重记录", "重"],
      ["selected-date.icon", "已选日期", "日"],
      ["action.add-plan.icon", "添加安排", "+"]
    ]
  },
  {
    pageCode: "square", pageName: "广场", pagePath: "/pages/square/index", pageGroup: "main",
    items: [
      ["search.icon", "搜索", "搜"],
      ["action.create-post.icon", "发布内容", "+"]
    ]
  },
  {
    pageCode: "service_shop", pageName: "商城", pagePath: "/pages/service-shop/index", pageGroup: "main",
    items: [
      ["search.icon", "搜索", "搜"],
      ["shortcut.new.icon", "新品上架", "NEW"],
      ["shortcut.live-feed.icon", "活饵", "虫"],
      ["shortcut.supplement.icon", "营养补剂", "钙"],
      ["shortcut.equipment.icon", "环境器材", "温"],
      ["shortcut.bedding.icon", "垫材清洁", "垫"],
      ["shortcut.bundle.icon", "套餐", "套"],
      ["shortcut.consult.icon", "活体咨询", "询"],
      ["shortcut.city.icon", "同城配送", "城"],
      ["shortcut.merchant.icon", "商户", "店"],
      ["shortcut.category.icon", "分类", "四"],
      ["topic.restock.icon", "补给专区", "盒"],
      ["topic.new.icon", "新品速递", "火"]
    ]
  },
  {
    pageCode: "mine", pageName: "我的", pagePath: "/pages/mine/index", pageGroup: "main",
    items: [
      ["quick.pet-profile.icon", "宠物资料", "宠"],
      ["quick.data-dashboard.icon", "数据总览", "数"],
      ["quick.ai.icon", "AI 健康分析", "AI"],
      ["menu.family.icon", "家庭空间", "家"],
      ["menu.form.icon", "表格导出", "表"],
      ["menu.cloud.icon", "云端数据", "云"],
      ["menu.theme.icon", "主题皮肤", "主"],
      ["menu.permission.icon", "会员权限", "权"],
      ["service.feedback.icon", "问题反馈", "反"],
      ["service.privacy.icon", "隐私协议", "隐"],
      ["service.about.icon", "关于我们", "关"],
      ["service.settings.icon", "设置", "设"]
    ]
  },
  {
    pageCode: "pet-profile", pageName: "宠物资料", pagePath: "/pkg-charts/pet-profile/index", pageGroup: "pet",
    items: [
      ["filter.all.icon", "全部宠物", "全"],
      ["profile.sex-male.icon", "公宠性别", "公"],
      ["profile.sex-female.icon", "母宠性别", "母"],
      ["profile.birth-date.icon", "出生日期", "生"],
      ["profile.keeping-duration.icon", "饲养时长", "养"],
      ["action.feeding.icon", "喂食记录", "喂"],
      ["action.cleaning.icon", "清洁记录", "洁"],
      ["action.shedding.icon", "蜕皮记录", "蜕"],
      ["action.weight.icon", "体重记录", "重"],
      ["section.today-reminder.icon", "今日提醒", "提"],
      ["state.completed.icon", "已完成", "完"],
      ["section.feeding-plan.icon", "喂食计划", "计"],
      ["section.environment.icon", "饲养环境", "环"],
      ["environment.temperature.icon", "温度", "温"],
      ["environment.humidity.icon", "湿度", "湿"],
      ["environment.lighting.icon", "光照", "光"],
      ["environment.substrate.icon", "垫材", "垫"]
    ]
  },
  {
    pageCode: "repti-lover-pet-create", pageName: "Repti Lover 新增宠物", pagePath: "/pages/repti-lover-pet-create/index", pageGroup: "pet",
    items: [
      ["suggestion.temperature.icon", "建议温度", "温"],
      ["suggestion.humidity.icon", "建议湿度", "湿"],
      ["suggestion.feeding.icon", "喂食间隔", "喂"],
      ["suggestion.observe.icon", "观察建议", "观"]
    ]
  },
  {
    pageCode: "repti-lover-album", pageName: "Repti Lover 相册", pagePath: "/pages/repti-lover-album/index", pageGroup: "pet",
    items: [
      ["summary.add-media.icon", "相册封面添加媒体", "+"],
      ["footer.add-media.icon", "底部添加媒体", "相"]
    ]
  },
  {
    pageCode: "native-tabbar", pageName: "原生底部导航", pagePath: "/pages.json", pageGroup: "system", runtimeMode: "COMPILE_TIME",
    items: [
      ["tab.home.icon", "首页", "首"],
      ["tab.home-selected.icon", "首页选中", "首"],
      ["tab.calendar.icon", "日历", "日"],
      ["tab.calendar-selected.icon", "日历选中", "日"],
      ["tab.square.icon", "广场", "广"],
      ["tab.square-selected.icon", "广场选中", "广"],
      ["tab.mine.icon", "我的", "我"],
      ["tab.mine-selected.icon", "我的选中", "我"]
    ]
  }
]

function createFeatureDraft() {
  return {
    featureKey: "",
    featureName: "",
    pageCode: "home",
    slotCode: "quick_feature.default",
    url: "",
    defaultIconType: "text",
    defaultIconText: "",
    defaultIconClass: "",
    defaultIconTextClass: "",
    defaultIconUrl: "",
    sortNo: 0,
    status: 1,
    isSystem: 0,
    remark: ""
  }
}

function createThemeIconDraft(featureKey = "") {
  return {
    featureKey,
    iconType: "image",
    iconText: "",
    iconClass: "",
    iconTextClass: "",
    iconUrl: "",
    status: 1
  }
}

function createThemeAssetDraft(assetKey = "") {
  return {
    editorKey: "",
    sourceType: "ASSET",
    featureKey: "",
    fallbackText: "",
    pageCode: "home",
    pageName: "",
    pagePath: "",
    pageGroup: "other",
    assetKey,
    assetName: "",
    assetType: "image",
    assetUrl: "",
    defaultAssetUrl: "",
    effectiveAssetUrl: "",
    runtimeMode: "REMOTE",
    configured: 0,
    textValue: "",
    sortNo: 0,
    status: 1
  }
}

function createPetTypeImageDraft(typeCode = "") {
  return {
    typeCode,
    typeName: "",
    parentTypeCode: "",
    levelNo: 1,
    defaultImageUrl: "",
    imageUrl: "",
    status: 0
  }
}

export default {
  name: "AppThemeView",
  data() {
    return {
      loading: false,
      deletingTheme: false,
      savingTheme: false,
      savingFeature: false,
      savingThemeIcon: false,
      savingThemeAsset: false,
      savingPetTypeImage: false,
      savingStyleTokens: false,
      selectedThemeCode: "",
      selectedFeatureKey: "",
      selectedAssetKey: "",
      selectedAssetPageCode: "",
      selectedPetTypeCode: "",
      themes: [],
      features: [],
      themeAssets: [],
      themePetTypeImages: [],
      themeStyleTokens: [],
      themeIconMap: {},
      themeSlots: [],
      iconPacks: [],
      selectedIconPackCode: "",
      selectedLayoutCode: "repti_lover",
      routeDrafts: [],
      homeSectionDrafts: [],
      loadingThemeDetail: false,
      savingLayout: false,
      themeDraft: createThemeDraft(),
      featureDraft: createFeatureDraft(),
      themeIconDraft: createThemeIconDraft(),
      assetDraft: createThemeAssetDraft(),
      petTypeImageDraft: createPetTypeImageDraft(),
      message: { type: "", text: "" }
    }
  },
  computed: {
    layoutPresets() {
      return LAYOUT_PRESETS
    },
    themeTabPages() {
      return THEME_TAB_PAGES
    },
    homeSectionDefinitions() {
      return HOME_SECTION_DEFINITIONS
    },
    selectedIconPack() {
      return this.iconPacks.find(item => item.packCode === this.selectedIconPackCode) || null
    },
    isEditingTheme() {
      return this.selectedThemeCode.length > 0
    },
    isEditingFeature() {
      return this.selectedFeatureKey.length > 0
    },
    canSaveThemeIcon() {
      return this.selectedThemeCode.length > 0 && this.featureDraft.featureKey.length > 0
    },
    canSaveThemeAsset() {
      return this.selectedThemeCode.length > 0 && this.assetDraft.editorKey.length > 0
    },
    configuredHomeFeatureKeys() {
      const slot = this.themeSlots.find(item =>
        item.pageCode === "home" && item.slotCode === "quick_feature.default" && item.status === 1
      )
      const items = slot && slot.config && Array.isArray(slot.config.items) ? slot.config.items : []
      return items
        .filter(item => item && Number(item.status) !== 0)
        .map(item => item.id || item.featureKey)
        .filter(Boolean)
    },
    interfaceIcons() {
      const catalogAssets = this.themeAssets
        .filter(item => item.assetType !== "background")
        .map(item => ({
          ...item,
          editorKey: `asset:${item.pageCode}:${item.assetKey}`,
          sourceType: "ASSET",
          featureKey: "",
          fallbackText: ""
        }))

      const assets = [...catalogAssets]
	      HOME_INTERFACE_ICON_FALLBACKS.forEach(definition => {
        if (assets.some(item => item.pageCode === "home" && item.assetKey === definition.assetKey)) return
        assets.push({
          editorKey: `asset:home:${definition.assetKey}`,
          sourceType: "ASSET",
          featureKey: "",
          pageCode: "home",
          pageName: "首页",
          pagePath: "/pages/app/index",
          pageGroup: "main",
          assetKey: definition.assetKey,
          assetName: definition.assetName,
          assetType: "image",
          assetUrl: "",
          defaultAssetUrl: "",
          effectiveAssetUrl: "",
          runtimeMode: "REMOTE",
          configured: 0,
          fallbackText: definition.fallbackText,
          textValue: "",
          sortNo: definition.sortNo,
          status: 1
	      })

	      if (this.selectedThemeCode === "repti_lover") {
	        REPTI_LOVER_INTERFACE_ICON_FALLBACKS.forEach(page => {
	          page.items.forEach((definition, index) => {
	            const [assetKey, assetName, fallbackText] = definition
	            if (assets.some(item => item.pageCode === page.pageCode && item.assetKey === assetKey)) return
	            assets.push({
	              editorKey: `asset:${page.pageCode}:${assetKey}`,
	              sourceType: "ASSET",
	              featureKey: "",
	              pageCode: page.pageCode,
	              pageName: page.pageName,
	              pagePath: page.pagePath,
	              pageGroup: page.pageGroup,
	              assetKey,
	              assetName,
	              assetType: "image",
	              assetUrl: "",
	              defaultAssetUrl: "",
	              effectiveAssetUrl: "",
	              runtimeMode: page.runtimeMode || "REMOTE",
	              configured: 0,
	              fallbackText,
	              textValue: "",
	              sortNo: (index + 1) * 10,
	              status: 1
	            })
	          })
	        })
	      }
      })

      const configuredKeys = new Set(this.configuredHomeFeatureKeys)
      const hasConfiguredList = configuredKeys.size > 0
      const featureIcons = this.features
        .filter(item => item.status === 1 && (!hasConfiguredList || configuredKeys.has(item.featureKey)))
        .map((item, index) => {
          const override = this.themeIconMap[item.featureKey]
          const packItem = this.selectedIconPack && Array.isArray(this.selectedIconPack.items)
            ? this.selectedIconPack.items.find(icon => icon.featureKey === item.featureKey && icon.status !== 0)
            : null
          const overrideActive = override && Number(override.status) !== 0
          const defaultUrl = (packItem && (packItem.iconUrl || packItem.iconImageUrl)) || item.defaultIconUrl || ""
          const fallbackText = (overrideActive && override.iconText)
            || (packItem && packItem.iconText)
            || item.defaultIconText
            || item.iconText
            || item.name.slice(0, 1)
          const assetUrl = overrideActive ? (override.iconUrl || override.iconImageUrl || "") : ""
          return {
            editorKey: `feature:${item.featureKey}`,
            sourceType: "FEATURE",
            featureKey: item.featureKey,
            pageCode: "home",
            pageName: "首页",
            pagePath: "/pages/app/index",
            pageGroup: "main",
            assetKey: `feature.${item.featureKey}.icon`,
            assetName: item.name,
            assetType: "image",
            assetUrl,
            defaultAssetUrl: defaultUrl,
            effectiveAssetUrl: assetUrl || defaultUrl,
            runtimeMode: "REMOTE",
            configured: overrideActive ? 1 : 0,
            fallbackText,
            textValue: "",
            sortNo: 1000 + (Number(item.sortNo) || index * 10),
            status: overrideActive ? Number(override.status) : 1
          }
        })
      return [...assets, ...featureIcons]
    },
    pageAssetPages() {
      const pageMap = new Map()
      this.interfaceIcons.forEach(item => {
        if (!pageMap.has(item.pageCode)) {
          pageMap.set(item.pageCode, {
            pageCode: item.pageCode,
            name: item.pageName || item.pageCode,
            pagePath: item.pagePath || "",
            pageGroup: item.pageGroup || "other",
            count: 0
          })
        }
        pageMap.get(item.pageCode).count += 1
      })
      return Array.from(pageMap.values())
    },
    selectedAssetPage() {
      return this.pageAssetPages.find(item => item.pageCode === this.selectedAssetPageCode) || null
    },
    visibleThemeAssets() {
      return this.themeAssets.filter(item => item.pageCode === this.selectedAssetPageCode)
    },
    visibleInterfaceIcons() {
      return this.interfaceIcons
        .filter(item => item.pageCode === this.selectedAssetPageCode)
        .sort((left, right) => Number(left.sortNo) - Number(right.sortNo))
    },
    compileTimeAssetCount() {
      return this.visibleInterfaceIcons.filter(item => item.runtimeMode === "COMPILE_TIME").length
    },
    canSavePetTypeImage() {
      return this.selectedThemeCode.length > 0 && this.petTypeImageDraft.typeCode.length > 0
    },
    isBackgroundAsset() {
      return this.assetDraft.assetType === "background" || this.assetDraft.assetKey === "page.background"
    }
  },
  created() {
    this.loadAll()
  },
  methods: {
    async loadAll() {
      this.loading = true
      this.message = { type: "", text: "" }
      try {
        await Promise.all([this.loadThemes(), this.loadFeatures()])
        if (!this.selectedThemeCode && this.themes.length > 0) {
          await this.selectTheme(this.themes[0].themeCode)
        }
        if (!this.selectedFeatureKey && this.features.length > 0) {
          this.selectFeature(this.features[0].featureKey)
        }
      } catch (error) {
        this.showError(error, "数据加载失败")
      } finally {
        this.loading = false
      }
    },
    async loadThemes() {
      const res = await fetchThemeList({ clientType: "MINI_APP" })
      this.themes = Array.isArray(res.data) ? res.data.map(item => this.normalizeTheme(item)) : []
    },
    async loadFeatures() {
      const res = await fetchAppFeatures({ pageCode: "home", slotCode: "quick_feature.default" })
      this.features = Array.isArray(res.data) ? res.data.map(item => this.normalizeFeature(item)) : []
      if (this.selectedFeatureKey) {
        this.selectFeature(this.selectedFeatureKey)
      }
    },
    async loadThemeIcons(themeCode) {
      if (!themeCode) {
        this.themeIconMap = {}
        return
      }
      try {
        const res = await fetchThemeFeatureIcons(themeCode)
        const nextMap = {}
        const list = Array.isArray(res.data) ? res.data : []
        list.forEach(item => {
          if (item && item.featureKey) {
            nextMap[item.featureKey] = item
          }
        })
        this.themeIconMap = nextMap
        this.syncThemeIconDraft()
      } catch (error) {
        this.themeIconMap = {}
        this.showError(error, "主题后备图标加载失败")
      }
    },
    async loadThemeDetail(themeCode) {
      if (!themeCode) return
      this.loadingThemeDetail = true
      try {
        const res = await fetchThemeDetail(themeCode)
        if (this.selectedThemeCode !== themeCode) return
        const detail = res.data || {}
        this.themeDraft = { ...createThemeDraft(), ...this.normalizeTheme(detail), slots: null }
        this.themeSlots = Array.isArray(detail.slots) ? detail.slots.map(item => this.normalizeThemeSlot(item)) : []
        this.iconPacks = Array.isArray(detail.iconPacks) ? detail.iconPacks.map(item => this.normalizeIconPack(item)) : []
        const defaultPack = this.iconPacks.find(item => item.isDefault === 1) || this.iconPacks[0]
        if (!this.iconPacks.some(item => item.packCode === this.selectedIconPackCode)) {
          this.selectedIconPackCode = defaultPack ? defaultPack.packCode : ""
        }
        this.syncLayoutDrafts()
      } catch (error) {
        this.showError(error, "主题详情加载失败")
      } finally {
        if (this.selectedThemeCode === themeCode) {
          this.loadingThemeDetail = false
        }
      }
    },
    async loadThemeAssets(themeCode) {
      if (!themeCode) {
        this.themeAssets = []
        this.selectedAssetKey = ""
        this.selectedAssetPageCode = ""
        this.assetDraft = createThemeAssetDraft()
        return
      }
      try {
        const res = await fetchThemeAssets(themeCode)
        this.themeAssets = Array.isArray(res.data) ? res.data.map(item => this.normalizeThemeAsset(item)) : []
        const pageExists = this.pageAssetPages.some(item => item.pageCode === this.selectedAssetPageCode)
        if (!pageExists) {
          this.selectedAssetPageCode = this.pageAssetPages.length > 0 ? this.pageAssetPages[0].pageCode : ""
        }
        const selectedExists = this.visibleInterfaceIcons.some(item => item.editorKey === this.selectedAssetKey)
        if (selectedExists) {
          const selected = this.visibleInterfaceIcons.find(item => item.editorKey === this.selectedAssetKey)
          this.selectInterfaceIcon(selected)
        } else if (this.visibleInterfaceIcons.length > 0) {
          this.selectInterfaceIcon(this.visibleInterfaceIcons[0])
        } else {
          this.selectedAssetKey = ""
          this.assetDraft = createThemeAssetDraft()
        }
      } catch (error) {
        this.showError(error, "主题素材加载失败")
      }
    },
    async selectTheme(themeCode) {
      const current = this.themes.find(item => item.themeCode === themeCode)
      if (!current) return
      this.selectedThemeCode = themeCode
      this.themeDraft = { ...createThemeDraft(), ...current, slots: null }
      this.themeIconMap = {}
      this.themeAssets = []
      this.themeSlots = []
      this.iconPacks = Array.isArray(current.iconPacks) ? current.iconPacks.map(item => this.normalizeIconPack(item)) : []
      const defaultPack = this.iconPacks.find(item => item.isDefault === 1) || this.iconPacks[0]
      this.selectedIconPackCode = defaultPack ? defaultPack.packCode : ""
      this.syncLayoutDrafts()
      await this.loadThemeDetail(themeCode)
      await Promise.all([
        this.loadThemeIcons(themeCode),
        this.loadThemeAssets(themeCode)
      ])
      const selectedIcon = this.interfaceIcons.find(item => item.editorKey === this.selectedAssetKey)
        || this.visibleInterfaceIcons[0]
      if (selectedIcon) this.selectInterfaceIcon(selectedIcon)
    },
    selectFeature(featureKey) {
      const current = this.features.find(item => item.featureKey === featureKey)
      if (!current) return
      this.selectedFeatureKey = featureKey
      this.featureDraft = {
        ...createFeatureDraft(),
        featureKey: current.featureKey,
        featureName: current.name,
        pageCode: current.pageCode || "home",
        slotCode: current.slotCode || "quick_feature.default",
        url: current.url || "",
        defaultIconType: current.defaultIconType || "text",
        defaultIconText: current.defaultIconText || current.iconText || "",
        defaultIconClass: current.defaultIconClass || "",
        defaultIconTextClass: current.defaultIconTextClass || "",
        defaultIconUrl: current.defaultIconUrl || "",
        sortNo: Number(current.sortNo) || 0,
        status: Number(current.status) === 0 ? 0 : 1,
        isSystem: Number(current.isSystem) === 1 ? 1 : 0,
        remark: current.remark || ""
      }
      this.syncThemeIconDraft()
    },
    selectAssetPage(pageCode) {
      this.selectedAssetPageCode = pageCode
      this.selectedAssetKey = ""
      if (this.visibleInterfaceIcons.length > 0) {
        this.selectInterfaceIcon(this.visibleInterfaceIcons[0])
      } else {
        this.assetDraft = createThemeAssetDraft()
      }
    },
    selectAsset(assetKey, pageCode = this.selectedAssetPageCode) {
      const current = this.themeAssets.find(item => item.pageCode === pageCode && item.assetKey === assetKey)
      if (!current) return
      this.selectedAssetPageCode = current.pageCode
      this.selectedAssetKey = assetKey
      this.assetDraft = {
        ...createThemeAssetDraft(assetKey),
        ...current,
        pageCode: current.pageCode || "home",
        assetType: current.assetType || "image",
        assetUrl: current.assetUrl || "",
        textValue: current.textValue || "",
        sortNo: Number(current.sortNo) || 0,
        status: Number(current.status) === 0 ? 0 : 1
      }
    },
    selectInterfaceIcon(item) {
      if (!item) return
      this.selectedAssetPageCode = item.pageCode || "home"
      this.selectedAssetKey = item.editorKey
      this.assetDraft = {
        ...createThemeAssetDraft(item.assetKey),
        ...item,
        editorKey: item.editorKey,
        sourceType: item.sourceType || "ASSET",
        featureKey: item.featureKey || "",
        pageCode: item.pageCode || "home",
        assetType: item.assetType || "image",
        assetUrl: item.assetUrl || "",
        defaultAssetUrl: item.defaultAssetUrl || "",
        effectiveAssetUrl: item.effectiveAssetUrl || item.assetUrl || item.defaultAssetUrl || "",
        textValue: item.textValue || item.fallbackText || "",
        sortNo: Number(item.sortNo) || 0,
        status: Number(item.status) === 0 ? 0 : 1
      }
    },
    async loadThemePetTypeImages(themeCode) {
      if (!themeCode) {
        this.themePetTypeImages = []
        this.selectedPetTypeCode = ""
        this.petTypeImageDraft = createPetTypeImageDraft()
        return
      }
      try {
        const res = await fetchThemePetTypeImages(themeCode)
        this.themePetTypeImages = Array.isArray(res.data) ? res.data.map(item => this.normalizeThemePetTypeImage(item)) : []
        if (this.selectedPetTypeCode && this.themePetTypeImages.some(item => item.typeCode === this.selectedPetTypeCode)) {
          this.selectPetTypeImage(this.selectedPetTypeCode)
        } else if (this.themePetTypeImages.length > 0) {
          this.selectPetTypeImage(this.themePetTypeImages[0].typeCode)
        } else {
          this.petTypeImageDraft = createPetTypeImageDraft()
        }
      } catch (error) {
        this.showError(error, "主题宠物头像加载失败")
      }
    },
    async loadThemeStyleTokens(themeCode) {
      if (!themeCode) {
        this.themeStyleTokens = []
        return
      }
      try {
        const res = await fetchThemeStyleTokens(themeCode, { pageCode: "home" })
        this.themeStyleTokens = Array.isArray(res.data) ? res.data.map(item => this.normalizeThemeStyleToken(item)) : []
      } catch (error) {
        this.showError(error, "界面样式加载失败")
      }
    },
    selectPetTypeImage(typeCode) {
      const current = this.themePetTypeImages.find(item => item.typeCode === typeCode)
      if (!current) return
      this.selectedPetTypeCode = typeCode
      this.petTypeImageDraft = {
        ...createPetTypeImageDraft(typeCode),
        ...current,
        status: Number(current.status) === 1 ? 1 : 0
      }
    },
    syncThemeIconDraft() {
      const icon = this.themeIconMap[this.selectedFeatureKey]
      this.themeIconDraft = icon
        ? {
            featureKey: icon.featureKey,
            iconType: icon.iconType || "image",
            iconText: icon.iconText || "",
            iconClass: icon.iconClass || "",
            iconTextClass: icon.iconTextClass || "",
            iconUrl: icon.iconUrl || icon.iconImageUrl || "",
            status: Number(icon.status) === 0 ? 0 : 1
          }
        : createThemeIconDraft(this.selectedFeatureKey)
    },
    startCreateTheme() {
      this.selectedThemeCode = ""
      this.themeIconMap = {}
      this.themeAssets = []
      this.selectedAssetKey = ""
      this.selectedAssetPageCode = ""
      this.themePetTypeImages = []
      this.selectedPetTypeCode = ""
      this.themeStyleTokens = []
      this.themeSlots = []
      this.iconPacks = []
      this.selectedIconPackCode = ""
      this.themeDraft = createThemeDraft()
      this.themeIconDraft = createThemeIconDraft(this.selectedFeatureKey)
      this.assetDraft = createThemeAssetDraft()
      this.petTypeImageDraft = createPetTypeImageDraft()
      this.syncLayoutDrafts()
    },
    resetThemeDraft() {
      if (this.selectedThemeCode) {
        this.selectTheme(this.selectedThemeCode)
        return
      }
      this.startCreateTheme()
    },
    startCreateFeature() {
      this.selectedFeatureKey = ""
      this.featureDraft = createFeatureDraft()
      this.themeIconDraft = createThemeIconDraft()
    },
    validateThemeDraft() {
      if (!this.themeDraft.themeCode || !this.themeDraft.themeName) {
        return "主题编码和主题名称不能为空"
      }
      return ""
    },
    validateFeatureDraft() {
      if (!this.featureDraft.featureKey || !this.featureDraft.featureName || !this.featureDraft.url) {
        return "功能编码、名称、跳转地址不能为空"
      }
      return ""
    },
    buildThemePayload(slots = null) {
      return {
        themeCode: this.themeDraft.themeCode,
        themeName: this.themeDraft.themeName,
        clientType: "MINI_APP",
        previewImageUrl: this.themeDraft.previewImageUrl || "",
        status: Number(this.themeDraft.status) === 0 ? 0 : 1,
        isDefault: Number(this.themeDraft.isDefault) === 1 ? 1 : 0,
        versionNo: Number(this.themeDraft.versionNo) > 0 ? Number(this.themeDraft.versionNo) : 1,
        remark: this.themeDraft.remark || "",
        slots
      }
    },
    syncLayoutDrafts() {
      const homeRouteSlot = this.findThemeSlot("home", "route.default")
      const layoutSlot = this.findThemeSlot("home", "layout.default")
      const homeRouteCode = this.normalizeRouteCode(homeRouteSlot && homeRouteSlot.config ? homeRouteSlot.config.routeCode : "")
      const matchedByRoute = LAYOUT_PRESETS.find(item => item.routeCode === homeRouteCode)
      const configuredLayoutCode = layoutSlot && layoutSlot.config ? layoutSlot.config.layoutCode : ""
      const matchedLayout = LAYOUT_PRESETS.find(item => item.layoutCode === configuredLayoutCode)
      const inferredFromTheme = LAYOUT_PRESETS.find(item => item.layoutCode === this.selectedThemeCode)
      const preset = matchedLayout || matchedByRoute || inferredFromTheme || LAYOUT_PRESETS[0]
      this.selectedLayoutCode = preset.layoutCode
      this.routeDrafts = THEME_TAB_PAGES.map(page => {
        const slot = this.findThemeSlot(page.pageCode, "route.default")
        const routeCode = this.normalizeRouteCode(slot && slot.config ? slot.config.routeCode : preset.routeCode)
        return { pageCode: page.pageCode, routeCode }
      })
      const sections = layoutSlot && layoutSlot.config && Array.isArray(layoutSlot.config.sections)
        ? layoutSlot.config.sections
        : []
      this.homeSectionDrafts = this.normalizeHomeSections(sections, preset.layoutCode)
    },
    findThemeSlot(pageCode, slotCode) {
      return this.themeSlots.find(item => item.pageCode === pageCode && item.slotCode === slotCode) || null
    },
    normalizeRouteCode(routeCode) {
      return LAYOUT_PRESETS.some(item => item.routeCode === routeCode) ? routeCode : "repti-lover"
    },
    resolveRouteDraftCode(pageCode) {
      const current = this.routeDrafts.find(item => item.pageCode === pageCode)
      return current ? current.routeCode : "repti-lover"
    },
    updateRouteDraft(pageCode, routeCode) {
      const current = this.routeDrafts.find(item => item.pageCode === pageCode)
      if (current) {
        current.routeCode = this.normalizeRouteCode(routeCode)
      } else {
        this.routeDrafts.push({ pageCode, routeCode: this.normalizeRouteCode(routeCode) })
      }
      if (pageCode === "home") {
        const preset = LAYOUT_PRESETS.find(item => item.routeCode === routeCode) || LAYOUT_PRESETS[0]
        this.selectedLayoutCode = preset.layoutCode
        this.homeSectionDrafts = this.defaultHomeSections(preset.layoutCode)
      }
    },
    applyLayoutPreset(layoutCode) {
      const preset = LAYOUT_PRESETS.find(item => item.layoutCode === layoutCode) || LAYOUT_PRESETS[0]
      this.selectedLayoutCode = preset.layoutCode
      this.routeDrafts = THEME_TAB_PAGES.map(page => ({ pageCode: page.pageCode, routeCode: preset.routeCode }))
      this.homeSectionDrafts = this.defaultHomeSections(preset.layoutCode)
    },
    defaultHomeSections(_layoutCode) {
      return [
        { key: "petPanel", variant: "hero" },
        { key: "quickFeature", variant: "compact" },
        { key: "actionBar", variant: "minimal" },
        { key: "snakeTool", variant: "hidden" }
      ].map((item, index) => ({ ...item, sortNo: (index + 1) * 10 }))
    },
    normalizeHomeSections(sections, layoutCode) {
      const fallback = this.defaultHomeSections(layoutCode)
      const normalized = []
      sections.forEach(item => {
        if (!item || !HOME_SECTION_DEFINITIONS.some(definition => definition.key === item.key)) return
        const definition = HOME_SECTION_DEFINITIONS.find(current => current.key === item.key)
        normalized.push({
          key: item.key,
          variant: definition.variants.includes(item.variant) ? item.variant : fallback.find(current => current.key === item.key).variant,
          sortNo: Number(item.sortNo) || 100
        })
      })
      fallback.forEach(item => {
        if (!normalized.some(current => current.key === item.key)) normalized.push(item)
      })
      return normalized.sort((a, b) => a.sortNo - b.sortNo).map((item, index) => ({ ...item, sortNo: (index + 1) * 10 }))
    },
    resolveHomeSectionName(key) {
      const current = HOME_SECTION_DEFINITIONS.find(item => item.key === key)
      return current ? current.name : key
    },
    resolveHomeSectionVariants(key) {
      const current = HOME_SECTION_DEFINITIONS.find(item => item.key === key)
      return current ? current.variants : ["default"]
    },
    resolveVariantName(variant) {
      const names = {
        expanded: "展开",
        compact: "紧凑",
        collapsed: "收起",
        hero: "主视觉",
        minimal: "极简",
        auto: "自动",
        hidden: "隐藏",
        grid: "宫格",
        focus: "突出",
        default: "默认"
      }
      return names[variant] || variant
    },
    moveHomeSection(index, direction) {
      const target = index + direction
      if (target < 0 || target >= this.homeSectionDrafts.length) return
      const next = this.homeSectionDrafts.slice()
      const current = next[index]
      next.splice(index, 1)
      next.splice(target, 0, current)
      this.homeSectionDrafts = next.map((item, currentIndex) => ({ ...item, sortNo: (currentIndex + 1) * 10 }))
    },
    buildThemeSlotPayloads() {
      const managedPages = THEME_TAB_PAGES.map(item => item.pageCode)
      const payloads = this.themeSlots
        .filter(item => !(item.slotCode === "route.default" && managedPages.includes(item.pageCode)))
        .filter(item => !(item.pageCode === "home" && item.slotCode === "layout.default"))
        .map(item => ({
          pageCode: item.pageCode,
          slotCode: item.slotCode,
          configType: item.configType || "JSON",
          configJson: JSON.stringify(item.config || {}),
          sortNo: Number(item.sortNo) || 0,
          status: Number(item.status) === 0 ? 0 : 1
        }))
      THEME_TAB_PAGES.forEach(page => {
        const routeCode = this.resolveRouteDraftCode(page.pageCode)
        payloads.push({
          pageCode: page.pageCode,
          slotCode: "route.default",
          configType: "JSON",
          configJson: JSON.stringify({
            routeCode,
            componentCode: `${page.slug}-${routeCode}`,
            componentPath: `/components/theme-tabs/${page.slug}/${routeCode}/index`,
            pagePath: page.pagePath,
            folderRule: "components/theme-tabs/{tabCode}/{routeCode}/index",
            mode: "tab-component"
          }),
          sortNo: 21,
          status: 1
        })
      })
      payloads.push({
        pageCode: "home",
        slotCode: "layout.default",
        configType: "JSON",
        configJson: JSON.stringify({
          layoutCode: this.selectedLayoutCode,
          sections: this.homeSectionDrafts.map((item, index) => ({ ...item, sortNo: (index + 1) * 10 }))
        }),
        sortNo: 25,
        status: 1
      })
      return payloads
    },
    buildFeaturePayload() {
      return {
        ...this.featureDraft,
        status: Number(this.featureDraft.status) === 0 ? 0 : 1,
        sortNo: Number(this.featureDraft.sortNo) || 0,
        isSystem: Number(this.featureDraft.isSystem) === 1 ? 1 : 0
      }
    },
    buildThemeIconPayload() {
      return {
        featureKey: this.featureDraft.featureKey,
        iconType: this.themeIconDraft.iconType,
        iconText: this.themeIconDraft.iconText,
        iconClass: this.themeIconDraft.iconClass,
        iconTextClass: this.themeIconDraft.iconTextClass,
        iconUrl: this.themeIconDraft.iconUrl,
        status: Number(this.themeIconDraft.status) === 0 ? 0 : 1
      }
    },
    buildThemeAssetPayload() {
      return {
        pageCode: this.assetDraft.pageCode || "home",
        assetKey: this.assetDraft.assetKey,
        assetName: this.assetDraft.assetName,
        assetType: this.assetDraft.assetType,
        assetUrl: this.assetDraft.assetUrl,
        textValue: this.assetDraft.textValue,
        status: Number(this.assetDraft.status) === 0 ? 0 : 1,
        sortNo: Number(this.assetDraft.sortNo) || 0
      }
    },
    buildPetTypeImagePayload() {
      return {
        typeCode: this.petTypeImageDraft.typeCode,
        imageUrl: this.petTypeImageDraft.imageUrl,
        status: Number(this.petTypeImageDraft.status) === 1 ? 1 : 0
      }
    },
    async saveThemeDraft() {
      const validationMessage = this.validateThemeDraft()
      if (validationMessage) {
        this.showMessage("error", validationMessage)
        return
      }
      this.savingTheme = true
      try {
        const res = await saveTheme(this.buildThemePayload())
        this.showMessage("success", res.message || "主题已保存")
        await this.loadThemes()
        await this.selectTheme((res.data && res.data.themeCode) || this.themeDraft.themeCode)
      } catch (error) {
        this.showError(error, "主题保存失败")
      } finally {
        this.savingTheme = false
      }
    },
    async deleteSelectedTheme() {
      if (!this.selectedThemeCode || this.deletingTheme) return
      const themeName = this.themeDraft.themeName || this.selectedThemeCode
      if (!window.confirm(`确定删除主题“${themeName}”吗？已选择该主题的用户会自动回到默认主题。`)) {
        return
      }
      this.deletingTheme = true
      try {
        const res = await deleteTheme(this.selectedThemeCode)
        this.showMessage("success", res.message || "主题已删除")
        this.selectedThemeCode = ""
        this.selectedAssetPageCode = ""
        this.selectedAssetKey = ""
        await this.loadThemes()
        if (this.themes.length > 0) {
          await this.selectTheme(this.themes[0].themeCode)
        } else {
          this.startCreateTheme()
        }
      } catch (error) {
        this.showError(error, "主题删除失败")
      } finally {
        this.deletingTheme = false
      }
    },
    async saveLayoutDraft() {
      if (!this.selectedThemeCode) {
        this.showMessage("error", "请先保存主题")
        return
      }
      if (this.loadingThemeDetail) {
        this.showMessage("error", "主题配置仍在加载，请稍后再试")
        return
      }
      this.savingLayout = true
      try {
        const res = await saveTheme(this.buildThemePayload(this.buildThemeSlotPayloads()))
        this.showMessage("success", res.message || "页面布局已保存")
        await this.loadThemeDetail(this.selectedThemeCode)
      } catch (error) {
        this.showError(error, "页面布局保存失败")
      } finally {
        this.savingLayout = false
      }
    },
    async saveFeatureDraft() {
      const validationMessage = this.validateFeatureDraft()
      if (validationMessage) {
        this.showMessage("error", validationMessage)
        return
      }
      this.savingFeature = true
      try {
        const res = await saveAppFeature(this.buildFeaturePayload())
        this.showMessage("success", res.message || "功能已保存")
        await this.loadFeatures()
        this.selectFeature((res.data && res.data.featureKey) || this.featureDraft.featureKey)
      } catch (error) {
        this.showError(error, "功能保存失败")
      } finally {
        this.savingFeature = false
      }
    },
    async saveThemeIconDraft() {
      if (!this.canSaveThemeIcon) {
        this.showMessage("error", "请先选择主题和功能")
        return
      }
      this.savingThemeIcon = true
      try {
        const res = await saveThemeFeatureIcon(this.selectedThemeCode, this.buildThemeIconPayload())
        this.showMessage("success", res.message || "主题图标已保存")
        await this.loadThemeIcons(this.selectedThemeCode)
      } catch (error) {
        this.showError(error, "主题图标保存失败")
      } finally {
        this.savingThemeIcon = false
      }
    },
    async saveThemeAssetDraft() {
      if (!this.canSaveThemeAsset) {
        this.showMessage("error", "请先选择主题和图标")
        return
      }
      this.savingThemeAsset = true
      try {
        const editorKey = this.assetDraft.editorKey
        if (this.assetDraft.sourceType === "FEATURE") {
          const feature = this.features.find(item => item.featureKey === this.assetDraft.featureKey) || {}
          const existing = this.themeIconMap[this.assetDraft.featureKey] || {}
          const hasOverride = Boolean(this.assetDraft.assetUrl)
          const res = await saveThemeFeatureIcon(this.selectedThemeCode, {
            featureKey: this.assetDraft.featureKey,
            iconType: hasOverride ? "image" : (existing.iconType || feature.defaultIconType || "text"),
            iconText: existing.iconText || feature.defaultIconText || feature.iconText || "",
            iconClass: existing.iconClass || feature.defaultIconClass || "",
            iconTextClass: existing.iconTextClass || feature.defaultIconTextClass || "",
            iconUrl: this.assetDraft.assetUrl || "",
            status: hasOverride && Number(this.assetDraft.status) !== 0 ? 1 : 0
          })
          this.showMessage("success", res.message || "主题图标已保存")
          await this.loadThemeIcons(this.selectedThemeCode)
        } else {
          const res = await saveThemeAsset(this.selectedThemeCode, this.buildThemeAssetPayload())
          this.showMessage("success", res.message || "主题图标已保存")
          await this.loadThemeAssets(this.selectedThemeCode)
        }
        const current = this.interfaceIcons.find(item => item.editorKey === editorKey)
        if (current) this.selectInterfaceIcon(current)
      } catch (error) {
        this.showError(error, "主题图标保存失败")
      } finally {
        this.savingThemeAsset = false
      }
    },
    async restoreCurrentIconDefault() {
      this.assetDraft.assetUrl = ""
      this.assetDraft.configured = 0
      if (this.assetDraft.sourceType === "FEATURE") {
        this.assetDraft.status = 0
      }
      await this.saveThemeAssetDraft()
    },
    async savePetTypeImageDraft() {
      if (!this.canSavePetTypeImage) {
        this.showMessage("error", "请先选择主题和宠物类型")
        return
      }
      this.savingPetTypeImage = true
      try {
        const res = await saveThemePetTypeImage(this.selectedThemeCode, this.buildPetTypeImagePayload())
        this.showMessage("success", res.message || "宠物类型头像已保存")
        const typeCode = this.petTypeImageDraft.typeCode
        await this.loadThemePetTypeImages(this.selectedThemeCode)
        this.selectPetTypeImage(typeCode)
      } catch (error) {
        this.showError(error, "宠物类型头像保存失败")
      } finally {
        this.savingPetTypeImage = false
      }
    },
    async saveStyleTokenDrafts(showSuccess = true, themeCode = "") {
      const targetThemeCode = themeCode || this.selectedThemeCode
      if (!targetThemeCode) {
        this.showMessage("error", "请先选择主题")
        return
      }
      this.savingStyleTokens = true
      try {
        for (const item of this.themeStyleTokens) {
          await saveThemeStyleToken(targetThemeCode, {
            pageCode: item.pageCode || "home",
            tokenKey: item.tokenKey,
            tokenName: item.tokenName,
            tokenValue: item.tokenValue,
            valueType: item.valueType,
            status: Number(item.status) === 0 ? 0 : 1,
            sortNo: Number(item.sortNo) || 0
          })
        }
        await this.loadThemeStyleTokens(targetThemeCode)
        if (showSuccess) {
          this.showMessage("success", "界面样式已保存")
        }
      } catch (error) {
        this.showError(error, "界面样式保存失败")
      } finally {
        this.savingStyleTokens = false
      }
    },
    async uploadIconTo(target, event) {
      const file = event && event.target && event.target.files ? event.target.files[0] : null
      if (!file) return
      try {
        const res = await uploadFeatureIcon(file)
        const url = res.data && res.data.url ? res.data.url : ""
        if (!url) return
        if (target === "preview") {
          this.themeDraft.previewImageUrl = url
        } else if (target === "theme") {
          this.themeIconDraft.iconType = "image"
          this.themeIconDraft.iconUrl = url
        } else if (target === "asset") {
          this.assetDraft.assetUrl = url
          if (this.assetDraft.assetType !== "background") {
            this.assetDraft.assetType = "image"
          }
        } else if (target === "petType") {
          this.petTypeImageDraft.imageUrl = url
          this.petTypeImageDraft.status = 1
        } else {
          this.featureDraft.defaultIconType = "image"
          this.featureDraft.defaultIconUrl = url
        }
        if (target === "preview") {
          this.showMessage("success", "预览图已上传，请保存基础设置")
          return
        }
        if (target === "asset" && this.canSaveThemeAsset) {
          await this.saveThemeAssetDraft()
          this.showMessage("success", "主题素材已上传并保存")
          return
        }
        if (target === "theme" && this.canSaveThemeIcon) {
          await this.saveThemeIconDraft()
          this.showMessage("success", "主题图标已上传并保存")
          return
        }
        if (target === "petType" && this.canSavePetTypeImage) {
          await this.savePetTypeImageDraft()
          this.showMessage("success", "宠物类型头像已上传并保存")
          return
        }
        if (target === "feature" && !this.validateFeatureDraft()) {
          await this.saveFeatureDraft()
          this.showMessage("success", "默认图标已上传并保存")
          return
        }
        this.showMessage("success", "图片已上传，请保存当前设置")
      } catch (error) {
        this.showError(error, "图标上传失败")
      } finally {
        if (event && event.target) {
          event.target.value = ""
        }
      }
    },
    uploadFeatureDefaultIcon(event) {
      this.uploadIconTo("feature", event)
    },
    uploadThemePreviewImage(event) {
      this.uploadIconTo("preview", event)
    },
    uploadThemeOverrideIcon(event) {
      this.uploadIconTo("theme", event)
    },
    uploadThemeAssetImage(event) {
      this.uploadIconTo("asset", event)
    },
    uploadThemePetTypeImage(event) {
      this.uploadIconTo("petType", event)
    },
    clearThemeIcon() {
      this.themeIconDraft = createThemeIconDraft(this.featureDraft.featureKey)
      this.themeIconDraft.status = 0
    },
    normalizeTheme(item) {
      return {
        themeCode: item.themeCode || "",
        themeName: item.themeName || "未命名主题",
        clientType: item.clientType || "MINI_APP",
        previewImageUrl: item.previewImageUrl || "",
        status: Number(item.status) === 0 ? 0 : 1,
        isDefault: Number(item.isDefault) === 1 ? 1 : 0,
        versionNo: Number(item.versionNo) > 0 ? Number(item.versionNo) : 1,
        remark: item.remark || "",
        iconPacks: Array.isArray(item.iconPacks) ? item.iconPacks.map(pack => this.normalizeIconPack(pack)) : []
      }
    },
    normalizeThemeSlot(item) {
      return {
        pageCode: item.pageCode || "home",
        pageName: item.pageName || item.pageCode || "未命名界面",
        pagePath: item.pagePath || "",
        pageGroup: item.pageGroup || "other",
        slotCode: item.slotCode || "",
        configType: item.configType || "JSON",
        config: item.config && typeof item.config === "object" ? item.config : {},
        sortNo: Number(item.sortNo) || 0,
        status: Number(item.status) === 0 ? 0 : 1
      }
    },
    normalizeIconPack(item) {
      return {
        ...item,
        packCode: item.packCode || "",
        packName: item.packName || item.packCode || "未命名图标包",
        previewImageUrl: item.previewImageUrl || "",
        status: Number(item.status) === 0 ? 0 : 1,
        isDefault: Number(item.isDefault) === 1 ? 1 : 0,
        sortNo: Number(item.sortNo) || 0,
        items: Array.isArray(item.items) ? item.items.map(icon => ({ ...icon, status: Number(icon.status) === 0 ? 0 : 1 })) : []
      }
    },
    normalizeFeature(item) {
      return {
        ...item,
        featureKey: item.featureKey || item.id || "",
        name: item.name || item.featureName || "未命名功能",
        status: Number(item.status) === 0 ? 0 : 1
      }
    },
    normalizeThemeAsset(item) {
      return {
        ...item,
        pageCode: item.pageCode || "home",
        assetKey: item.assetKey || "",
        assetName: item.assetName || "未命名素材",
        assetType: item.assetType || "image",
        assetUrl: item.assetUrl || "",
        defaultAssetUrl: item.defaultAssetUrl || "",
        effectiveAssetUrl: item.effectiveAssetUrl || item.assetUrl || item.defaultAssetUrl || "",
        runtimeMode: item.runtimeMode || "REMOTE",
        configured: Number(item.configured) === 1 ? 1 : 0,
        textValue: item.textValue || "",
        sortNo: Number(item.sortNo) || 0,
        status: Number(item.status) === 0 ? 0 : 1
      }
    },
    normalizeThemePetTypeImage(item) {
      return {
        ...item,
        typeCode: item.typeCode || "",
        typeName: item.typeName || "未命名类型",
        parentTypeCode: item.parentTypeCode || "",
        levelNo: Number(item.levelNo) || 1,
        defaultImageUrl: item.defaultImageUrl || "",
        imageUrl: item.imageUrl || "",
        status: Number(item.status) === 1 ? 1 : 0
      }
    },
    normalizeThemeStyleToken(item) {
      return {
        ...item,
        pageCode: item.pageCode || "home",
        tokenKey: item.tokenKey || "",
        tokenName: item.tokenName || item.tokenKey || "样式项",
        tokenValue: item.tokenValue || "",
        valueType: item.valueType || "text",
        status: Number(item.status) === 0 ? 0 : 1,
        sortNo: Number(item.sortNo) || 0
      }
    },
    resolveFeaturePreview(item) {
      return item.defaultIconUrl || item.iconImageUrl || ""
    },
    resolveFeatureEditorIcon() {
      const featureKey = this.featureDraft.featureKey
      const packItem = this.selectedIconPack && Array.isArray(this.selectedIconPack.items)
        ? this.selectedIconPack.items.find(item => item.featureKey === featureKey && item.status !== 0)
        : null
      if (packItem) {
        return {
          url: packItem.iconUrl || packItem.iconImageUrl || "",
          text: packItem.iconText || this.featureDraft.defaultIconText || "图",
          source: `${this.selectedIconPack.packName}图标包`
        }
      }
      if (this.themeIconDraft.status !== 0 && (this.themeIconDraft.iconUrl || this.themeIconDraft.iconText)) {
        return {
          url: this.themeIconDraft.iconUrl || "",
          text: this.themeIconDraft.iconText || this.featureDraft.defaultIconText || "图",
          source: "主题后备图标"
        }
      }
      return {
        url: this.featureDraft.defaultIconUrl || "",
        text: this.featureDraft.defaultIconText || "图",
        source: "功能默认图标"
      }
    },
    resolvePreviewUrl(path) {
      return resolveBackendAssetUrl(path)
    },
    buildThemePreviewStyle(seedText, previewImageUrl = "") {
      const palette = this.resolvePalette(seedText)
      const previewUrl = this.resolvePreviewUrl(previewImageUrl)
      if (previewUrl) {
        return {
          backgroundColor: palette[0],
          backgroundImage: `url(${previewUrl})`,
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover"
        }
      }
      return {
        background: `linear-gradient(135deg, ${palette[0]} 0%, ${palette[1]} 56%, ${palette[2]} 100%)`
      }
    },
    buildAssetFallbackText(item) {
      if (item && item.fallbackText) return item.fallbackText
      const name = item && item.assetName ? item.assetName : "图"
      return name.slice(0, 1)
    },
    resolveAssetDisplayUrl(item) {
      if (!item) return ""
      return item.assetUrl || item.defaultAssetUrl || ""
    },
    buildAssetBackgroundPreviewStyle() {
      const imageUrl = this.resolveAssetDisplayUrl(this.assetDraft)
      if (!imageUrl) {
        return {}
      }
      return {
        backgroundImage: `url(${this.resolvePreviewUrl(imageUrl)})`
      }
    },
    resolveStyleTokenValue(tokenKey, fallback) {
      const current = this.themeStyleTokens.find(item => item.tokenKey === tokenKey)
      return current && current.tokenValue ? current.tokenValue : fallback
    },
    buildStylePreviewPageStyle() {
      return {
        background: `linear-gradient(135deg, ${this.resolveStyleTokenValue("primaryColor", "#2B5FFF")} 0%, #f8fafc 100%)`
      }
    },
    buildStylePreviewCardStyle() {
      const bg = this.resolveStyleTokenValue("surfaceBg", "#FFFFFF")
      const alpha = this.resolveStyleTokenValue("surfaceAlpha", "0.16")
      const border = this.resolveStyleTokenValue("surfaceBorder", "#FFFFFF")
      const borderAlpha = this.resolveStyleTokenValue("surfaceBorderAlpha", "0.60")
      const blur = this.resolveStyleTokenValue("glassBlur", "22")
      return {
        background: this.hexToRgba(bg, alpha),
        borderColor: this.hexToRgba(border, borderAlpha),
        backdropFilter: `blur(${blur}px)`
      }
    },
    hexToRgba(hex, alpha) {
      const normalized = /^#[0-9a-fA-F]{6}$/.test(hex || "") ? hex : "#FFFFFF"
      const parsedAlpha = Number(alpha)
      const opacity = Number.isFinite(parsedAlpha) ? Math.max(0, Math.min(1, parsedAlpha)) : 1
      const red = parseInt(normalized.slice(1, 3), 16)
      const green = parseInt(normalized.slice(3, 5), 16)
      const blue = parseInt(normalized.slice(5, 7), 16)
      return `rgba(${red}, ${green}, ${blue}, ${opacity})`
    },
    resolvePalette(seedText) {
      const palettes = [
        ["#0f172a", "#2563eb", "#7dd3fc"],
        ["#1f2937", "#10b981", "#bef264"],
        ["#78350f", "#f59e0b", "#fde68a"],
        ["#312e81", "#fb7185", "#fed7aa"],
        ["#134e4a", "#38bdf8", "#ccfbf1"]
      ]
      const text = (seedText || "default").trim()
      let hash = 0
      for (let index = 0; index < text.length; index += 1) {
        hash = (hash * 31 + text.charCodeAt(index)) % palettes.length
      }
      return palettes[Math.abs(hash) % palettes.length]
    },
    showMessage(type, text) {
      this.message = { type, text }
    },
    showError(error, fallback) {
      this.message = {
        type: "error",
        text: error && error.message ? error.message : fallback
      }
    }
  }
}
</script>

<style scoped>
.theme-page {
  height: 100%;
  min-height: 0;
  display: grid;
  grid-template-columns: 318px minmax(0, 1fr);
  gap: 18px;
  overflow: hidden;
}

.theme-sidebar,
.theme-workbench {
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.theme-toolbar,
.head-actions,
.theme-card__head,
.theme-card__badges,
.workbench-head,
.panel-head,
.upload-card {
  display: flex;
  align-items: center;
}

.theme-toolbar {
  justify-content: space-between;
  margin-bottom: 14px;
  color: #64748b;
  font-size: 13px;
}

.theme-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
}

.theme-card {
  width: 100%;
  display: grid;
  grid-template-columns: 74px minmax(0, 1fr);
  gap: 12px;
  padding: 12px;
  border: 1px solid #e8edf3;
  border-radius: 18px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  margin-bottom: 12px;
}

.theme-card--active {
  border-color: #2563eb;
  box-shadow: 0 14px 30px rgba(37, 99, 235, 0.14);
}

.theme-card__art {
  height: 74px;
  border-radius: 16px;
}

.theme-card__main {
  min-width: 0;
}

.theme-card__head {
  justify-content: space-between;
  gap: 8px;
}

.theme-card__meta {
  margin-top: 8px;
  color: #64748b;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.theme-card__badges {
  gap: 6px;
  margin-top: 10px;
}

.workbench-head {
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;
}

.head-actions {
  gap: 10px;
}

.theme-message {
  flex-shrink: 0;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 14px;
  font-size: 13px;
}

.theme-message--success {
  color: #047857;
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
}

.theme-message--error {
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.theme-strip {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: 1.1fr 1.1fr 120px 100px 1.8fr;
  gap: 12px;
  margin-top: 14px;
  padding: 14px;
  border: 1px solid #e8edf3;
  border-radius: 18px;
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
}

.workbench-tabs {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.workbench-tab {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 13px 15px;
  border: 1px solid #e2e8f0;
  border-radius: 17px;
  background: #f8fafc;
  text-align: left;
  cursor: pointer;
}

.workbench-tab strong {
  color: #0f172a;
  font-size: 14px;
}

.workbench-tab span {
  color: #64748b;
  font-size: 12px;
}

.workbench-tab--active {
  border-color: #2563eb;
  background: linear-gradient(180deg, #eef4ff 0%, #ffffff 100%);
  box-shadow: 0 12px 24px rgba(37, 99, 235, 0.1);
}

.workbench-body {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(300px, 34%) minmax(0, 1fr);
  gap: 16px;
  margin-top: 16px;
  overflow: hidden;
}

.feature-edit-stack {
  min-height: 0;
  overflow: hidden;
  display: grid;
  grid-template-rows: minmax(320px, 1fr) minmax(240px, 0.72fr);
  gap: 16px;
}

.feature-panel,
.feature-detail-panel,
.editor-panel,
.theme-icon-panel,
.asset-panel,
.asset-editor-panel {
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid #e8edf3;
  border-radius: 20px;
  background: #fff;
}

.feature-edit-stack {
  border: 0;
  border-radius: 0;
  background: transparent;
}

.panel-head {
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #edf2f7;
}

.panel-head h3 {
  margin: 0;
  color: #0f172a;
  font-size: 16px;
}

.panel-head p {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 12px;
}

.feature-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  box-sizing: border-box;
  padding: 12px;
  scroll-padding-bottom: 44px;
}

.feature-list::after {
  content: "";
  display: block;
  height: 44px;
}

.feature-row {
  width: 100%;
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border: 1px solid transparent;
  border-radius: 16px;
  background: #f8fafc;
  margin-bottom: 10px;
  cursor: pointer;
  text-align: left;
}

.feature-row:last-child {
  margin-bottom: 0;
}

.feature-row--active {
  background: #eef4ff;
  border-color: #9bbcff;
}

.feature-row__main {
  min-width: 0;
}

.feature-row__title {
  color: #0f172a;
  font-weight: 700;
}

.feature-row__meta {
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.feature-icon,
.upload-preview,
.phone-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #111827;
  color: #fff;
  font-weight: 800;
}

.feature-icon {
  width: 44px;
  height: 44px;
  border-radius: 15px;
}

.feature-icon img,
.upload-preview img,
.phone-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.editor-panel,
.theme-icon-panel,
.asset-editor-panel {
  overflow-y: auto;
}

.asset-workbench {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(320px, 36%) minmax(0, 1fr);
  gap: 16px;
  margin-top: 16px;
  overflow: hidden;
}

.pet-type-workbench,
.style-workbench {
  flex: 1;
  min-height: 0;
  display: grid;
  gap: 16px;
  margin-top: 16px;
  overflow: hidden;
}

.pet-type-workbench {
  grid-template-columns: minmax(320px, 36%) minmax(0, 1fr);
}

.style-workbench {
  grid-template-columns: minmax(0, 1fr) 360px;
}

.asset-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  box-sizing: border-box;
  padding: 12px;
  scroll-padding-bottom: 44px;
}

.asset-list::after {
  content: "";
  display: block;
  height: 44px;
}

.asset-row {
  width: 100%;
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 11px;
  border: 1px solid transparent;
  border-radius: 17px;
  background: #f8fafc;
  margin-bottom: 10px;
  cursor: pointer;
  text-align: left;
}

.asset-row:last-child {
  margin-bottom: 0;
}

.asset-row--active {
  background: #eef4ff;
  border-color: #9bbcff;
}

.asset-thumb,
.asset-entry-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #0f172a;
  color: #fff;
  font-weight: 800;
}

.asset-thumb {
  width: 56px;
  height: 56px;
  border-radius: 17px;
}

.asset-thumb--background {
  width: 56px;
  height: 40px;
  border-radius: 14px;
  background: linear-gradient(135deg, #dbeafe, #fef3c7);
}

.asset-thumb img,
.asset-entry-icon img,
.asset-background-preview {
  width: 100%;
  height: 100%;
}

.asset-thumb img,
.asset-entry-icon img {
  object-fit: contain;
}

.asset-row__main {
  min-width: 0;
}

.asset-row__title {
  color: #0f172a;
  font-weight: 800;
}

.asset-row__meta {
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.asset-preview-block {
  padding: 16px 16px 0;
}

.asset-background-preview {
  display: flex;
  min-height: 180px;
  align-items: center;
  justify-content: center;
  border-radius: 24px;
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-weight: 800;
  background:
    radial-gradient(circle at top left, rgba(223, 205, 173, 0.38), transparent 36%),
    radial-gradient(circle at bottom right, rgba(190, 216, 234, 0.34), transparent 34%),
    #f5f7fa;
  background-position: center top;
  background-repeat: no-repeat;
  background-size: cover;
}

.asset-entry-preview {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border-radius: 24px;
  background: linear-gradient(135deg, #fffdf8 0%, #ffffff 46%, #f3f7ff 100%);
  border: 1px solid #e8edf4;
}

.asset-entry-preview p {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 12px;
}

.asset-entry-icon {
  width: 58px;
  height: 58px;
  flex-shrink: 0;
  border-radius: 18px;
}

.pet-type-image-preview {
  width: 168px;
  height: 132px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 24px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-weight: 800;
  overflow: hidden;
}

.pet-type-image-preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.style-panel,
.style-preview-panel {
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid #e8edf3;
  border-radius: 20px;
  background: #fff;
}

.style-token-grid {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 16px;
}

.style-token-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 92px;
  grid-template-rows: auto auto;
  gap: 4px 12px;
  align-items: center;
  padding: 13px;
  border: 1px solid #e8edf3;
  border-radius: 17px;
  background: #f8fafc;
}

.style-token-card span {
  color: #0f172a;
  font-weight: 800;
}

.style-token-card small {
  grid-column: 1;
  color: #64748b;
  font-size: 12px;
}

.style-token-card input {
  grid-column: 2;
  grid-row: 1 / span 2;
  width: 92px;
  height: 38px;
  border: 1px solid #d7deea;
  border-radius: 12px;
  background: #fff;
  box-sizing: border-box;
}

.style-action-bar {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  padding: 0 16px 16px;
}

.style-preview-phone {
  margin: 16px;
  flex: 1;
  min-height: 260px;
  border-radius: 28px;
  padding: 24px;
  box-sizing: border-box;
}

.style-preview-card {
  min-height: 180px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 24px;
  padding: 18px;
}

.style-preview-title {
  font-size: 20px;
  font-weight: 900;
}

.style-preview-desc {
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.6;
}

.style-preview-button {
  display: inline-flex;
  margin-top: 20px;
  min-height: 36px;
  padding: 0 16px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  color: #fff;
  font-weight: 800;
}

.upload-preview--soft {
  background: #eef4ff;
  color: #2563eb;
}

.editor-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 16px;
}

.editor-grid--single {
  grid-template-columns: 1fr;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
}

.field span {
  color: #334155;
  font-size: 12px;
  font-weight: 700;
}

.field input,
.field select {
  width: 100%;
  height: 39px;
  border: 1px solid #d7deea;
  border-radius: 13px;
  padding: 0 11px;
  color: #0f172a;
  background: #fff;
  box-sizing: border-box;
}

.field--wide {
  grid-column: span 2;
}

.upload-card {
  gap: 12px;
  margin: 0 16px 16px;
  padding: 14px;
  border-radius: 18px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
}

.upload-card p {
  margin: 5px 0 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
}

.upload-card--vertical {
  align-items: stretch;
  flex-direction: column;
}

.upload-preview {
  width: 58px;
  height: 58px;
  flex-shrink: 0;
  border-radius: 18px;
}

.upload-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 13px;
  background: #0f172a;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  overflow: hidden;
}

.upload-button--wide {
  width: 100%;
}

.upload-button input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.phone-preview {
  padding: 16px 16px 0;
}

.phone-card {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  grid-template-rows: auto auto;
  column-gap: 14px;
  align-items: center;
  padding: 22px 12px;
  border-radius: 24px;
  background:
    radial-gradient(circle at top left, rgba(125, 211, 252, 0.28), transparent 44%),
    linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
}

.phone-icon {
  grid-row: 1 / span 2;
  width: 72px;
  height: 72px;
  border-radius: 24px;
}

.phone-title {
  margin-top: 0;
  color: #0f172a;
  font-weight: 800;
}

.phone-desc {
  margin-top: 5px;
  color: #64748b;
  font-size: 12px;
}

.pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #475569;
  font-size: 11px;
  font-weight: 800;
  white-space: nowrap;
}

.pill--green {
  background: #dcfce7;
  color: #166534;
}

.pill--blue {
  background: #dbeafe;
  color: #1d4ed8;
}

@media (max-width: 1320px) {
  .theme-page {
    grid-template-columns: 260px minmax(0, 1fr);
  }

  .workbench-body {
    grid-template-columns: 280px minmax(0, 1fr);
  }

  .asset-workbench {
    grid-template-columns: 300px minmax(0, 1fr);
  }

  .pet-type-workbench {
    grid-template-columns: 300px minmax(0, 1fr);
  }

  .style-workbench {
    grid-template-columns: minmax(0, 1fr) 320px;
  }

  .theme-strip {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .theme-strip .field:last-child {
    grid-column: 1 / -1;
  }
}

@media (max-width: 1080px) {
  .theme-page {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }

  .theme-sidebar {
    min-height: 260px;
    max-height: 320px;
  }

  .theme-workbench {
    min-height: 760px;
  }

  .workbench-body {
    grid-template-columns: 1fr;
    grid-template-rows: 320px minmax(420px, 1fr) minmax(360px, 0.8fr);
    overflow-y: auto;
  }

  .asset-workbench {
    grid-template-columns: 1fr;
    grid-template-rows: 300px minmax(460px, 1fr);
    overflow-y: auto;
  }

  .pet-type-workbench,
  .style-workbench {
    grid-template-columns: 1fr;
    grid-template-rows: 300px minmax(460px, 1fr);
    overflow-y: auto;
  }

  .feature-panel,
  .editor-panel,
  .theme-icon-panel,
  .asset-panel,
  .asset-editor-panel {
    grid-column: 1;
    grid-row: auto;
  }

  .theme-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .workbench-tabs {
    grid-template-columns: 1fr;
  }
}

/* Compact operation layout */
.theme-page {
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 14px;
}

.theme-workbench {
  padding: 18px;
}

.page-card__desc,
.panel-head p,
.upload-card p,
.asset-entry-preview p,
.style-preview-panel .panel-head {
  display: none;
}

.theme-card,
.workbench-tab,
.feature-row,
.asset-row,
.feature-panel,
.editor-panel,
.theme-icon-panel,
.asset-panel,
.asset-editor-panel,
.style-panel,
.style-preview-panel,
.theme-strip,
.upload-card,
.phone-card,
.asset-entry-preview,
.style-token-card {
  border-radius: 8px;
}

.theme-card {
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 8px;
  padding: 9px;
  margin-bottom: 8px;
}

.theme-card__art {
  height: 42px;
  border-radius: 8px;
}

.theme-card__badges {
  margin-top: 7px;
}

.theme-toolbar {
  margin-bottom: 10px;
}

.workbench-head {
  align-items: flex-start;
}

.head-actions {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.theme-strip {
  grid-template-columns: 170px 180px 96px 86px minmax(180px, 1fr);
  gap: 10px;
  margin-top: 12px;
  padding: 12px;
  background: #f8fafc;
}

.workbench-tabs {
  display: flex;
  gap: 6px;
  margin-top: 12px;
  padding: 5px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f1f5f9;
}

.workbench-tab {
  min-height: 36px;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  border-color: transparent;
  background: transparent;
  text-align: center;
}

.workbench-tab span {
  display: none;
}

.workbench-tab--active {
  background: #ffffff;
  border-color: #d7deea;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.workbench-body,
.asset-workbench,
.pet-type-workbench,
.style-workbench {
  margin-top: 12px;
}

.workbench-body {
  grid-template-columns: minmax(320px, 36%) minmax(0, 1fr);
}

.feature-edit-stack {
  grid-template-columns: minmax(360px, 1fr) minmax(300px, 0.72fr);
  grid-template-rows: minmax(0, 1fr);
  gap: 12px;
}

.feature-detail-panel {
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid #e8edf3;
  background: #fff;
}

.feature-detail-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.panel-head--sticky {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #ffffff;
}

.inline-upload-bar {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
  padding: 0 12px 12px;
}

.section-divider {
  margin: 2px 12px 0;
  padding-top: 12px;
  border-top: 1px solid #edf2f7;
  color: #0f172a;
  font-size: 13px;
  font-weight: 800;
}

.feature-list,
.asset-list {
  padding: 10px;
}

.feature-list::after,
.asset-list::after {
  height: 16px;
}

.feature-row,
.asset-row {
  position: relative;
  width: 100%;
  box-sizing: border-box;
  border-color: #e8edf3;
  margin-bottom: 8px;
}

.feature-row {
  grid-template-columns: 44px minmax(0, 1fr) auto 56px;
}

.asset-row {
  grid-template-columns: 50px minmax(0, 1fr) auto 56px;
}

.feature-row:hover,
.asset-row:hover {
  border-color: #bcd0f7;
  background: #f5f8ff;
}

.row-action {
  position: sticky;
  right: 0;
  z-index: 1;
  min-width: 52px;
  height: 32px;
  border: 1px solid #d7deea;
  border-radius: 8px;
  background: #ffffff;
  color: #2563eb;
  font-weight: 700;
  cursor: pointer;
}

.feature-icon {
  width: 42px;
  height: 42px;
  border-radius: 8px;
}

.asset-thumb {
  width: 48px;
  height: 48px;
  border-radius: 8px;
}

.asset-thumb--background {
  width: 48px;
  height: 36px;
}

.panel-head {
  min-height: 58px;
  padding: 12px 14px;
}

.panel-head h3 {
  font-size: 15px;
}

.editor-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  padding: 12px;
}

.editor-grid--single {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.editor-grid--single .field:nth-child(2),
.editor-grid--single .field:nth-child(3) {
  grid-column: 1 / -1;
}

.field input,
.field select {
  height: 36px;
  border-radius: 8px;
}

.upload-card {
  margin: 0 12px 12px;
  padding: 12px;
  justify-content: space-between;
}

.upload-button {
  min-height: 36px;
  border-radius: 8px;
}

.phone-preview,
.asset-preview-block {
  padding: 12px 12px 0;
}

.phone-card {
  grid-template-columns: 56px minmax(0, 1fr);
  padding: 14px 12px;
}

.phone-icon {
  width: 56px;
  height: 56px;
  border-radius: 8px;
}

.asset-background-preview {
  min-height: 150px;
  border-radius: 8px;
}

.asset-entry-preview {
  padding: 14px;
}

.asset-entry-icon {
  width: 52px;
  height: 52px;
  border-radius: 8px;
}

.pet-type-image-preview {
  width: 148px;
  height: 112px;
  border-radius: 8px;
}

.style-token-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  padding: 12px;
}

.style-token-card {
  padding: 10px;
}

@media (max-width: 1500px) {
  .feature-edit-stack {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(320px, 1fr) minmax(260px, 0.7fr);
  }

  .style-token-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 1320px) {
  .theme-page {
    grid-template-columns: 220px minmax(0, 1fr);
  }

  .theme-strip {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .theme-strip .field:last-child {
    grid-column: 1 / -1;
  }

  .feature-row {
    grid-template-columns: 42px minmax(0, 1fr) auto;
  }

  .asset-row {
    grid-template-columns: 48px minmax(0, 1fr) auto;
  }

  .row-action {
    display: none;
  }
}

@media (max-width: 1080px) {
  .theme-page {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }

  .theme-workbench {
    min-height: 760px;
  }

  .workbench-tabs {
    overflow-x: auto;
  }

  .workbench-tab {
    min-width: 104px;
  }
}

/* Logic-aligned theme editor */
.theme-page {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: hidden;
  color: #18212f;
}

.theme-selector {
  min-height: 92px;
  flex-shrink: 0;
  display: flex;
  align-items: stretch;
  gap: 14px;
  padding: 12px 14px;
  border-radius: 8px;
  overflow: hidden;
}

.theme-selector__summary {
  width: 112px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-right: 1px solid #e6eaf0;
}

.theme-selector__summary .page-card__title {
  font-size: 16px;
}

.theme-selector__summary .page-card__desc {
  display: block;
  margin-top: 5px;
  font-size: 12px;
}

.theme-selector__state {
  flex: 1;
  display: flex;
  align-items: center;
  color: #778397;
  font-size: 13px;
}

.theme-list {
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 8px;
  padding: 0 2px 4px 0;
  overflow-x: auto;
  overflow-y: hidden;
}

.theme-card {
  width: 168px;
  min-width: 168px;
  height: 66px;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  align-items: center;
  gap: 9px;
  padding: 8px;
  margin: 0;
  border: 1px solid #dfe4eb;
  border-radius: 8px;
  background: #fff;
  box-shadow: none;
}

.theme-card:hover {
  border-color: #aebbd0;
  background: #f9fbfd;
}

.theme-card--active {
  border-color: #2563eb;
  background: #f4f7ff;
  box-shadow: inset 0 0 0 1px #2563eb;
}

.theme-card__art {
  width: 42px;
  height: 48px;
  border-radius: 6px;
}

.theme-card__head {
  min-width: 0;
  gap: 6px;
}

.theme-card__head strong {
  min-width: 0;
  overflow: hidden;
  color: #18212f;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.theme-card__default {
  flex-shrink: 0;
  padding: 2px 5px;
  border-radius: 4px;
  background: #e7f7ee;
  color: #147a45;
  font-size: 10px;
  font-weight: 700;
}

.theme-card__meta {
  margin-top: 6px;
  font-size: 11px;
}

.theme-selector__actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 12px;
  border-left: 1px solid #e6eaf0;
}

.theme-selector__actions .ghost-button,
.theme-selector__actions .action-button {
  height: 34px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 12px;
}

.theme-workbench {
  flex: 1;
  min-height: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  overflow: hidden;
}

.workbench-head {
  min-height: 68px;
  padding: 12px 16px;
  border: 1px solid #e0e5ec;
  border-radius: 8px 8px 0 0;
  background: #fff;
}

.workbench-title {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 11px;
}

.theme-identity-editor {
  min-width: 0;
}

.theme-name-input,
.theme-code-input {
  height: 34px;
  border: 1px solid #d8dee8;
  border-radius: 6px;
  background: #fff;
  color: #18212f;
  font-size: 13px;
}

.theme-name-input {
  width: min(280px, 34vw);
  padding: 0 9px;
  font-size: 16px;
  font-weight: 700;
}

.theme-code-input {
  width: 150px;
  padding: 0 9px;
}

.theme-name-input:focus,
.theme-code-input:focus {
  border-color: #2563eb;
  outline: 0;
}

.theme-preview-dot {
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.72);
  box-shadow: 0 0 0 1px #d9e0e9;
}

.workbench-title .page-card__title {
  max-width: 360px;
  overflow: hidden;
  font-size: 17px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.workbench-title__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 5px;
  color: #778397;
  font-size: 11px;
}

.status-dot {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.status-dot::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #a7b0bf;
}

.status-dot--active::before {
  background: #23a566;
}

.status-dot--default {
  padding: 2px 6px;
  border-radius: 4px;
  background: #edf3ff;
  color: #245cc3;
}

.status-dot--default::before {
  display: none;
}

.workbench-head .ghost-button,
.workbench-head .action-button,
.panel-head .ghost-button,
.panel-head .action-button {
  height: 34px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 12px;
}

.theme-message {
  margin: 8px 0 0;
  padding: 8px 12px;
  border-radius: 6px;
}

.workbench-tabs {
  min-height: 48px;
  display: flex;
  gap: 2px;
  margin: 0;
  padding: 6px 10px 0;
  border: 1px solid #e0e5ec;
  border-top: 0;
  border-radius: 0 0 8px 8px;
  background: #fff;
  overflow-x: auto;
}

.workbench-tab {
  min-width: 104px;
  min-height: 40px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  border: 0;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  text-align: center;
}

.workbench-tab:hover {
  background: #f6f8fb;
}

.workbench-tab strong {
  font-size: 13px;
  font-weight: 600;
}

.workbench-tab span {
  display: none;
}

.workbench-tab--active {
  border-color: #2563eb;
  background: #f5f8ff;
  box-shadow: none;
}

.workbench-tab--active strong {
  color: #1d5ac8;
}

.theme-basic-workbench,
.layout-workbench,
.workbench-body,
.asset-workbench,
.pet-type-workbench,
.style-workbench {
  flex: 1;
  min-height: 0;
  display: grid;
  gap: 12px;
  margin: 0;
  padding: 12px 0 0;
  overflow: hidden;
}

.theme-basic-workbench {
  grid-template-columns: minmax(280px, 0.8fr) minmax(420px, 1.4fr);
}

.layout-workbench {
  grid-template-columns: minmax(520px, 1.55fr) minmax(300px, 0.8fr);
}

.workbench-body {
  grid-template-columns: minmax(290px, 0.72fr) minmax(470px, 1.35fr);
}

.asset-workbench,
.pet-type-workbench {
  grid-template-columns: minmax(290px, 0.72fr) minmax(470px, 1.35fr);
}

.style-workbench {
  grid-template-columns: minmax(520px, 1.4fr) minmax(280px, 0.7fr);
}

.theme-basic-preview,
.theme-basic-editor,
.layout-preset-panel,
.home-order-panel,
.feature-panel,
.feature-detail-panel,
.asset-panel,
.asset-editor-panel,
.style-panel,
.style-preview-panel {
  min-width: 0;
  min-height: 0;
  border: 1px solid #e0e5ec;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.theme-basic-preview,
.theme-basic-editor,
.layout-preset-panel,
.home-order-panel,
.feature-detail-panel,
.asset-editor-panel,
.style-panel,
.style-preview-panel {
  display: flex;
  flex-direction: column;
}

.panel-head {
  min-height: 54px;
  padding: 10px 13px;
  border-bottom: 1px solid #e7ebf0;
}

.panel-head h3 {
  font-size: 14px;
}

.panel-head p,
.upload-card p,
.asset-entry-preview p {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.45;
}

.theme-basic-preview {
  padding: 14px;
  overflow-y: auto;
}

.theme-basic-preview__art {
  min-height: 180px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 14px;
  border-radius: 8px;
  color: #fff;
  overflow: hidden;
}

.theme-basic-preview__chrome {
  display: flex;
  gap: 5px;
}

.theme-basic-preview__chrome span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.74);
}

.theme-basic-preview__content {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 12px;
  border-radius: 6px;
  background: rgba(11, 18, 29, 0.7);
}

.theme-basic-preview__content strong {
  font-size: 17px;
}

.theme-basic-preview__content span {
  max-width: 100%;
  overflow: hidden;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.theme-facts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1px;
  margin: 12px 0 0;
  border: 1px solid #e5e9ef;
  border-radius: 6px;
  background: #e5e9ef;
  overflow: hidden;
}

.theme-facts div {
  padding: 9px;
  background: #f8fafc;
}

.theme-facts dt {
  color: #7b8798;
  font-size: 10px;
}

.theme-facts dd {
  margin: 4px 0 0;
  color: #202a38;
  font-size: 12px;
  font-weight: 700;
}

.theme-basic-form {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-content: start;
  gap: 12px;
  padding: 14px;
  overflow-y: auto;
}

.input-with-action {
  display: flex;
  gap: 7px;
}

.input-with-action input {
  flex: 1;
  min-width: 0;
}

.upload-button--field {
  min-height: 36px;
  border-radius: 6px;
}

.setting-toggle {
  min-height: 58px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 38px;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #e2e7ed;
  border-radius: 8px;
  cursor: pointer;
}

.setting-toggle > span {
  display: flex;
  flex-direction: column;
  gap: 3px;
  color: #243041;
  font-size: 12px;
  font-weight: 700;
}

.setting-toggle small {
  color: #7b8798;
  font-size: 10px;
  font-weight: 400;
}

.setting-toggle input {
  position: absolute;
  opacity: 0;
}

.setting-toggle i {
  position: relative;
  width: 38px;
  height: 22px;
  border-radius: 11px;
  background: #c8d0db;
  transition: background 0.18s ease;
}

.setting-toggle i::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.18s ease;
}

.setting-toggle input:checked + i {
  background: #2563eb;
}

.setting-toggle input:checked + i::after {
  transform: translateX(16px);
}

.layout-preset-panel,
.home-order-panel {
  overflow-y: auto;
}

.layout-preset-panel .panel-head,
.home-order-panel .panel-head {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
}

.layout-empty-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7b8798;
  font-size: 13px;
}

.layout-preset-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  padding: 12px;
}

.layout-preset-card {
  position: relative;
  min-width: 0;
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr) 18px;
  align-items: center;
  gap: 10px;
  padding: 9px;
  border: 1px solid #dfe4eb;
  border-radius: 8px;
  background: #fff;
  text-align: left;
  cursor: pointer;
}

.layout-preset-card:hover {
  border-color: #aebbd0;
}

.layout-preset-card--active {
  border-color: #2563eb;
  background: #f4f7ff;
}

.layout-preset-card__preview {
  width: 54px;
  height: 58px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3px;
  padding: 6px;
  border-radius: 6px;
  background: #edf1f5;
}

.layout-preset-card__preview i {
  display: block;
  border-radius: 2px;
  background: #9babc0;
}

.layout-preset-card__preview--classic i:first-child,
.layout-preset-card__preview--pet_first i:first-child,
.layout-preset-card__preview--repti_lover i:first-child {
  grid-column: span 2;
}

.layout-preset-card__preview--tool_dashboard i:first-child {
  grid-row: span 2;
}

.layout-preset-card__preview--compact_tools {
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
}

.layout-preset-card__preview--pet_first {
  background: #e7f5ed;
}

.layout-preset-card__preview--tool_dashboard {
  background: #e9f0fb;
}

.layout-preset-card__preview--compact_tools {
  background: #e7f4f3;
}

.layout-preset-card__preview--repti_lover {
  background: #eef1df;
}

.layout-preset-card__copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.layout-preset-card__copy strong {
  color: #202a38;
  font-size: 12px;
}

.layout-preset-card__copy small {
  color: #7b8798;
  font-size: 10px;
  line-height: 1.4;
}

.layout-preset-card__check {
  color: transparent;
  font-size: 14px;
  font-weight: 800;
}

.layout-preset-card--active .layout-preset-card__check {
  color: #2563eb;
}

.layout-section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-top: 1px solid #e7ebf0;
  background: #f8fafc;
}

.layout-section-title div {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.layout-section-title strong {
  font-size: 12px;
}

.layout-section-title span {
  color: #7b8798;
  font-size: 10px;
}

.tab-route-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 7px;
  padding: 10px 12px 12px;
}

.tab-route-item {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.tab-route-item span {
  color: #657184;
  font-size: 10px;
}

.tab-route-item select {
  width: 100%;
  height: 32px;
  padding: 0 5px;
  border: 1px solid #d8dee7;
  border-radius: 6px;
  background: #fff;
  color: #263244;
  font-size: 10px;
}

.home-section-list {
  padding: 10px;
  overflow-y: auto;
}

.home-section-row {
  display: grid;
  grid-template-columns: 26px minmax(0, 1fr) 78px 58px;
  align-items: center;
  gap: 7px;
  min-height: 54px;
  padding: 7px;
  border-bottom: 1px solid #e9edf2;
}

.home-section-row:last-child {
  border-bottom: 0;
}

.home-section-row__index {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  background: #eef2f7;
  color: #657184;
  font-size: 11px;
  font-weight: 700;
}

.home-section-row__main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.home-section-row__main strong {
  font-size: 12px;
}

.home-section-row__main small {
  overflow: hidden;
  color: #8792a2;
  font-size: 9px;
  text-overflow: ellipsis;
}

.home-section-row__variant {
  width: 78px;
  height: 30px;
  border: 1px solid #d8dee7;
  border-radius: 6px;
  background: #fff;
  color: #344054;
  font-size: 10px;
}

.order-actions {
  display: flex;
  gap: 4px;
}

.order-actions button {
  width: 27px;
  height: 27px;
  padding: 0;
  border: 1px solid #d8dee7;
  border-radius: 5px;
  background: #fff;
  color: #4d5b70;
  cursor: pointer;
}

.order-actions button:disabled {
  opacity: 0.35;
  cursor: default;
}

.icon-source-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-bottom: 1px solid #e7ebf0;
  background: #f8fafc;
  overflow-x: auto;
}

.icon-source-bar__label,
.icon-source-bar__empty {
  flex-shrink: 0;
  color: #778397;
  font-size: 10px;
}

.icon-pack-chip {
  height: 26px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  padding: 0 8px;
  border: 1px solid #d8dee7;
  border-radius: 5px;
  background: #fff;
  color: #344054;
  font-size: 10px;
  cursor: pointer;
}

.icon-pack-chip small {
  color: #7b8798;
  font-size: 9px;
}

.icon-pack-chip--active {
  border-color: #2563eb;
  background: #edf3ff;
  color: #1f59be;
}

.icon-priority-line {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 10px 12px 0;
  padding: 7px 9px;
  border-radius: 6px;
  background: #f3f5f8;
  color: #657184;
  font-size: 10px;
}

.icon-priority-line i {
  color: #a0a9b7;
  font-style: normal;
}

.feature-list,
.asset-list,
.feature-detail-body,
.asset-editor-panel,
.style-token-grid {
  min-height: 0;
  overflow-y: auto;
}

.feature-list,
.asset-list {
  padding: 8px;
}

.feature-list::after,
.asset-list::after {
  height: 8px;
}

.feature-row,
.asset-row {
  min-width: 0;
  margin-bottom: 6px;
  padding: 8px;
  border-color: #e4e8ee;
  border-radius: 7px;
  background: #fbfcfd;
}

.feature-row {
  grid-template-columns: 38px minmax(0, 1fr) auto 46px;
}

.asset-row {
  grid-template-columns: 44px minmax(0, 1fr) auto 46px;
}

.feature-row--active,
.asset-row--active {
  border-color: #84a7eb;
  background: #f2f6ff;
}

.feature-icon {
  width: 36px;
  height: 36px;
  border-radius: 7px;
}

.asset-thumb {
  width: 42px;
  height: 42px;
  border-radius: 7px;
}

.asset-thumb--background {
  width: 42px;
  height: 32px;
  border-radius: 6px;
}

.feature-row__title,
.asset-row__title {
  font-size: 12px;
}

.feature-row__meta,
.asset-row__meta {
  font-size: 10px;
}

.row-action {
  position: static;
  min-width: 42px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  font-size: 10px;
}

.phone-preview,
.asset-preview-block {
  padding: 10px 12px 0;
}

.phone-card,
.asset-entry-preview {
  border-radius: 8px;
}

.phone-card {
  grid-template-columns: 46px minmax(0, 1fr);
  padding: 10px;
}

.phone-icon {
  width: 46px;
  height: 46px;
  border-radius: 8px;
}

.asset-background-preview {
  min-height: 120px;
  border-radius: 8px;
}

.section-divider {
  margin: 2px 12px 0;
  padding-top: 10px;
  font-size: 12px;
}

.editor-grid {
  gap: 9px;
  padding: 10px 12px;
}

.field {
  gap: 5px;
}

.field span {
  font-size: 10px;
}

.field input,
.field select {
  height: 34px;
  border-radius: 6px;
  font-size: 11px;
}

.inline-upload-bar {
  padding: 0 12px 10px;
}

.upload-card {
  margin: 0 12px 12px;
  padding: 10px;
  border-radius: 8px;
}

.upload-button {
  min-height: 32px;
  border-radius: 6px;
  font-size: 11px;
}

.style-token-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  padding: 10px;
}

.style-token-card {
  min-height: 58px;
  padding: 9px;
  border-radius: 7px;
}

.style-token-card span {
  font-size: 11px;
}

.style-token-card small {
  font-size: 9px;
}

.style-preview-phone {
  min-height: 210px;
  margin: 12px;
  padding: 16px;
  border-radius: 8px;
}

.style-preview-card {
  min-height: 150px;
  padding: 14px;
  border-radius: 8px;
}

.danger-button {
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid #efb3b3;
  border-radius: 6px;
  background: #fff7f7;
  color: #b42318;
  font-size: 11px;
  cursor: pointer;
}

.danger-button:disabled {
  opacity: 0.55;
  cursor: default;
}

.page-icon-workbench {
  min-height: 0;
  flex: 1;
  display: grid;
  grid-template-columns: 220px minmax(300px, 0.9fr) minmax(360px, 1.1fr);
  gap: 10px;
  overflow: hidden;
}

.page-catalog-panel,
.page-icon-list-panel,
.page-icon-workbench .asset-editor-panel {
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e1e6ed;
  border-radius: 8px;
  background: #fff;
}

.page-catalog-list,
.page-icon-grid {
  min-height: 0;
  overflow-y: auto;
  padding: 8px;
}

.page-catalog-row {
  width: 100%;
  min-height: 54px;
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) 24px;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  padding: 7px;
  border: 1px solid transparent;
  border-radius: 7px;
  background: #f8fafc;
  text-align: left;
  cursor: pointer;
}

.page-catalog-row--active {
  border-color: #7fa2e8;
  background: #eef4ff;
}

.page-catalog-row__icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: #243041;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
}

.page-catalog-row__main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.page-catalog-row__main strong {
  color: #243041;
  font-size: 11px;
}

.page-catalog-row__main small {
  overflow: hidden;
  color: #7b8798;
  font-size: 9px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.page-catalog-row__count {
  min-width: 22px;
  color: #64748b;
  font-size: 10px;
  text-align: center;
}

.compile-time-note {
  flex-shrink: 0;
  padding: 4px 6px;
  border-radius: 5px;
  background: #fff4e5;
  color: #9a5b13;
  font-size: 9px;
}

.page-icon-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-content: start;
  gap: 7px;
}

.page-icon-card {
  position: relative;
  min-width: 0;
  min-height: 86px;
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: 1px solid #e2e7ed;
  border-radius: 7px;
  background: #fbfcfd;
  text-align: left;
  cursor: pointer;
}

.page-icon-card--active {
  border-color: #2563eb;
  background: #f1f5ff;
}

.page-icon-card__preview {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 7px;
  background: #edf1f5;
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
}

.page-icon-card__preview--background {
  height: 36px;
}

.page-icon-card__preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.page-icon-card__preview--background img {
  object-fit: cover;
}

.page-icon-card__copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.page-icon-card__copy strong {
  overflow: hidden;
  color: #253044;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.page-icon-card__copy small {
  color: #7b8798;
  font-size: 9px;
}

.page-icon-card__mode {
  position: absolute;
  top: 5px;
  right: 5px;
  color: #9a5b13;
  font-size: 8px;
}

@media (max-width: 1180px) {
  .theme-page {
    height: auto;
    min-height: 100%;
    overflow: visible;
  }

  .theme-selector {
    min-height: 88px;
  }

  .theme-selector__summary {
    width: 96px;
  }

  .theme-workbench {
    min-height: 760px;
    overflow: visible;
  }

  .theme-basic-workbench,
  .layout-workbench,
  .workbench-body,
  .asset-workbench,
  .pet-type-workbench,
  .style-workbench {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
    overflow: visible;
  }

  .page-icon-workbench {
    grid-template-columns: 200px minmax(300px, 1fr);
    grid-template-rows: 380px auto;
    overflow: visible;
  }

  .page-icon-workbench .asset-editor-panel {
    grid-column: 1 / -1;
  }

  .theme-basic-preview,
  .feature-panel,
  .asset-panel,
  .home-order-panel,
  .style-preview-panel {
    min-height: 260px;
  }

  .theme-basic-editor,
  .layout-preset-panel,
  .feature-detail-panel,
  .asset-editor-panel,
  .style-panel {
    min-height: 420px;
  }

  .tab-route-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .theme-selector {
    flex-wrap: wrap;
  }

  .theme-selector__summary {
    width: auto;
    flex: 1;
    border-right: 0;
  }

  .theme-list {
    order: 3;
    width: 100%;
    flex-basis: 100%;
  }

  .theme-selector__actions {
    border-left: 0;
  }

  .workbench-head {
    align-items: stretch;
    flex-direction: column;
  }

  .head-actions {
    justify-content: flex-start;
  }

  .theme-name-input {
    width: min(260px, 66vw);
  }

  .theme-basic-form,
  .layout-preset-grid,
  .editor-grid,
  .style-token-grid {
    grid-template-columns: 1fr;
  }

  .field--wide {
    grid-column: auto;
  }

  .tab-route-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .page-icon-workbench {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
  }

  .page-catalog-panel,
  .page-icon-list-panel,
  .page-icon-workbench .asset-editor-panel {
    min-height: 320px;
    grid-column: auto;
  }
}
</style>
